import React, { useState, useEffect, useRef } from 'react';
import { Form, InputGroup, Button, Badge, Spinner, Modal } from 'react-bootstrap';
import { 
  Send, Search, Check, CheckCheck, RefreshCw, 
  MessageSquare, Lock, Unlock, AlertCircle, CheckCheck as CheckAll,
  Mail, UserCheck, CheckCircle2, ArrowRightLeft, Clock, Eye, Image, X, Plus, FileText, Globe
} from 'lucide-react';
import { 
  getWhatsAppChats, getWhatsAppMessages, sendWhatsAppFreeText, sendWhatsAppImage,
  markAllWhatsAppAsRead, resolveWhatsAppQuote, markWhatsAppChatAsUnread,
  resetWhatsAppChatAI, acceptWhatsAppChat, resolveWhatsAppChat, reassignWhatsAppChat,
  resolveAllWhatsAppChats, sendWhatsAppTemplate, searchWhatsAppContacts, translateWhatsAppText, API_BASE_URL
} from '../api';
import useAuthStore from '../../store/useAuthStore';
import { toast } from 'react-toastify';
import { playLocutoraAlertSound } from '../utils/soundAlerts';

const getAvatarColor = (name = '') => {
  const colors = [
    '#2563eb', '#7c3aed', '#db2777', '#ea580c', 
    '#059669', '#0891b2', '#4f46e5', '#0d9488'
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const getContactInitial = (name = '', phone = '') => {
  if (name && name.trim()) {
    const clean = name.trim().replace(/^[^a-zA-Z0-9]+/, '');
    if (clean) return clean.charAt(0).toUpperCase();
  }
  if (phone) {
    const digits = phone.replace(/\D/g, '');
    return digits ? digits.slice(-1) : '?';
  }
  return '?';
};

const WhatsAppChatCenter = () => {
  const user = useAuthStore(state => state.user);
  const rawOperatorName = user?.name || user?.full_name || user?.username || (user?.email ? user.email.split('@')[0] : 'Locutora');
  const operatorName = rawOperatorName.trim().split(' ')[0]; // Solo primer nombre, sin apellido
  const userRole = (user?.role || '').toLowerCase();
  // Perfiles con visión global de supervisión: admin, gestor, operaciones (jefa de operaciones)
  const isSupervisor = ['admin', 'gestor', 'operaciones', 'superadmin'].some(r => userRole.includes(r));

  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingChats, setLoadingChats] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [markingAllRead, setMarkingAllRead] = useState(false);

  // Message inputs
  const [messageText, setMessageText] = useState('');
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [selectedImagePreview, setSelectedImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  // Traductor instantáneo (entrante -> Español / saliente -> idioma del pasajero)
  const [messageTranslations, setMessageTranslations] = useState({});
  const [translatingMsgId, setTranslatingMsgId] = useState(null);
  const [outboundTargetLang, setOutboundTargetLang] = useState('auto_passenger');
  const [translatingDraft, setTranslatingDraft] = useState(false);

  // Filters & Tabs estilo Whaticket
  const [tabFilter, setTabFilter] = useState('pending'); // 'pending', 'mine', 'resolved', 'all'
  const [channelFilter, setChannelFilter] = useState('all'); // 'all', 'whatsapp', 'booking_chat'
  const [filterTodayOnly, setFilterTodayOnly] = useState(false);
  const [filterUnreadOnly, setFilterUnreadOnly] = useState(false);
  const [filterQuotesOnly, setFilterQuotesOnly] = useState(false);
  const [filterAttentionOnly, setFilterAttentionOnly] = useState(false);
  const [processingTicketAction, setProcessingTicketAction] = useState(false);

  const isBookingChatItem = (chatObj) => {
    if (!chatObj) return false;
    return (
      chatObj.channel === 'booking_chat' ||
      chatObj.contact_type === 'booking_live_chat' ||
      String(chatObj.phone_number || '').startsWith('booking:')
    );
  };

  const isTransfeeroChatItem = (chatObj) => {
    if (!chatObj) return false;
    return (
      chatObj.channel === 'transfeero_alert' ||
      chatObj.contact_type === 'transfeero_alert' ||
      String(chatObj.phone_number || '').toLowerCase().startsWith('transfeero:')
    );
  };

  // Modal Iniciar Conversación / Plantilla de 2 Variables Editables
  const [showInitTemplateModal, setShowInitTemplateModal] = useState(false);
  const [initModalMode, setInitModalMode] = useState('search'); // 'search' (desde botón +) o 'chat' (desde chat abierto)
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [contactSearchResults, setContactSearchResults] = useState([]);
  const [searchingContacts, setSearchingContacts] = useState(false);
  const [templatePhone, setTemplatePhone] = useState('');
  const [templateVar1Name, setTemplateVar1Name] = useState('');
  const [templateVar2Message, setTemplateVar2Message] = useState('');
  const [sendingInitTemplate, setSendingInitTemplate] = useState(false);

  // Búsqueda global en TODO el historial (más allá de los 100 recientes, solo lectura sin enviar plantilla)
  const [remoteSearchChats, setRemoteSearchChats] = useState(null);
  const [searchingRemoteChats, setSearchingRemoteChats] = useState(false);
  const [showHistorySearchModal, setShowHistorySearchModal] = useState(false);
  const [historySearchQuery, setHistorySearchQuery] = useState('');
  const [historySearchResults, setHistorySearchResults] = useState([]);
  const [searchingHistoryModal, setSearchingHistoryModal] = useState(false);
  
  // Auto-polling interval reference
  const pollingRef = useRef(null);
  const messageEndRef = useRef(null);

  const selectedChatRef = useRef(selectedChat);
  useEffect(() => {
    selectedChatRef.current = selectedChat;
    setSelectedImageFile(null);
    setSelectedImagePreview(null);
  }, [selectedChat]);

  const prevAttentionPhonesRef = useRef(null);

  // Load chats on mount and poll with document visibility guard
  useEffect(() => {
    fetchChats(true);
    
    pollingRef.current = setInterval(() => {
      if (document.hidden) return;
      fetchChats(false);
      
      if (selectedChatRef.current?.phone_number) {
        fetchMessages(selectedChatRef.current.phone_number, false);
      }
    }, 8000);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentChatPhone = selectedChat?.phone_number;
  useEffect(() => {
    if (currentChatPhone) {
      fetchMessages(currentChatPhone, true);
      setMessageText('');

      setChats(prev => prev.map(chat => {
        if (chat.phone_number === currentChatPhone) {
          const wasUnread = (chat.unread_count > 0 || chat.requires_attention);
          return { 
            ...chat, 
            unread_count: 0, 
            requires_attention: false,
            last_read_by: wasUnread && operatorName ? operatorName : (chat.last_read_by || operatorName),
            last_read_at: wasUnread ? new Date().toISOString() : (chat.last_read_at || new Date().toISOString())
          };
        }
        return chat;
      }));
      window.dispatchEvent(new CustomEvent('whatsapp_unread_updated'));
    } else {
      setMessages([]);
      setHasMoreMessages(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChatPhone]);

  const prevMessagesCountRef = useRef(0);
  useEffect(() => {
    if (!loadingOlder && messages.length > 0) {
      if (messages.length !== prevMessagesCountRef.current) {
        scrollToBottom();
        prevMessagesCountRef.current = messages.length;
      }
    }
  }, [messages, loadingOlder]);

  const scrollToBottom = () => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchChats = async (showSpinner = false) => {
    if (showSpinner) setLoadingChats(true);
    try {
      const data = await getWhatsAppChats(100, 0);
      const activePhone = selectedChatRef.current?.phone_number;

      // Sonido de alerta automática cuando un nuevo chat requiere atención humana
      const currentNeedingAttention = new Set(
        (data || [])
          .filter(c => (c.requires_attention || c.conversation_state === 'TRANSFERRED_LOCUTORA'))
          .map(c => c.phone_number)
      );

      if (prevAttentionPhonesRef.current !== null) {
        const hasNewAttentionChat = Array.from(currentNeedingAttention).some(
          phone => !prevAttentionPhonesRef.current.has(phone) && phone !== activePhone
        );
        if (hasNewAttentionChat) {
          playLocutoraAlertSound();
        }
      }
      prevAttentionPhonesRef.current = currentNeedingAttention;

      const sanitizedData = (data || []).map(c => {
        if (activePhone && c.phone_number === activePhone) {
          return { 
            ...c, 
            unread_count: 0, 
            requires_attention: false,
            last_read_by: c.last_read_by || selectedChatRef.current?.last_read_by,
            last_read_at: c.last_read_at || selectedChatRef.current?.last_read_at
          };
        }
        return c;
      });
      // Si hay un chat histórico abierto que no está entre los 100 más recientes, mantenerlo visible en memoria
      if (activePhone && selectedChatRef.current && !sanitizedData.some(c => c.phone_number === activePhone)) {
        sanitizedData.unshift({
          ...selectedChatRef.current,
          unread_count: 0,
          requires_attention: false
        });
      }
      setChats(sanitizedData);
      window.dispatchEvent(new CustomEvent('whatsapp_unread_updated'));
      
      if (activePhone) {
        const updated = (data || []).find(c => c.phone_number === activePhone);
        if (updated) {
          setSelectedChat(prev => {
            if (!prev) return { ...updated, unread_count: 0, requires_attention: false };
            return {
              ...prev,
              ...updated,
              unread_count: 0,
              requires_attention: false,
              last_read_by: updated.last_read_by || prev.last_read_by,
              last_read_at: updated.last_read_at || prev.last_read_at
            };
          });
        }
      }
    } catch (error) {
      console.error("Error loading chats:", error);
    } finally {
      if (showSpinner) setLoadingChats(false);
    }
  };

  const fetchMessages = async (phoneNumber, showSpinner = true) => {
    if (showSpinner) setLoadingMessages(true);
    try {
      const data = await getWhatsAppMessages(phoneNumber, 50, null, operatorName);
      setMessages(prev => {
        const newIds = (data || []).map(m => m.id).join(',');
        const oldIds = prev.map(m => m.id).join(',');
        return newIds !== oldIds ? (data || []) : prev;
      });
      setHasMoreMessages(Boolean(data && data.length === 50));
    } catch (error) {
      if (showSpinner) {
        toast.error("No se pudo cargar el historial de mensajes.");
      }
    } finally {
      if (showSpinner) setLoadingMessages(false);
    }
  };

  const loadOlderMessages = async () => {
    if (!selectedChat || messages.length === 0 || loadingOlder) return;
    setLoadingOlder(true);
    const oldestId = messages[0]?.id;
    try {
      const older = await getWhatsAppMessages(selectedChat.phone_number, 50, oldestId, operatorName);
      if (older && older.length > 0) {
        setMessages(prev => [...older, ...prev]);
        if (older.length < 50) {
          setHasMoreMessages(false);
        }
      } else {
        setHasMoreMessages(false);
      }
    } catch (error) {
      toast.error("Error al cargar mensajes anteriores.");
    } finally {
      setLoadingOlder(false);
    }
  };

  const handleSelectImageFile = (file) => {
    if (!file) return;
    if (!file.type || !file.type.startsWith('image/')) {
      toast.warning("Por favor selecciona un archivo de imagen válido (JPG, PNG, WEBP).");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.warning("La imagen supera el límite máximo permitido por WhatsApp (5 MB).");
      return;
    }
    if (selectedImagePreview) {
      URL.revokeObjectURL(selectedImagePreview);
    }
    setSelectedImageFile(file);
    setSelectedImagePreview(URL.createObjectURL(file));
  };

  const handleClearImage = () => {
    if (selectedImagePreview) {
      URL.revokeObjectURL(selectedImagePreview);
    }
    setSelectedImageFile(null);
    setSelectedImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePasteImage = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type && items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        if (file) {
          e.preventDefault();
          handleSelectImageFile(file);
          break;
        }
      }
    }
  };

  const handleSendFreeText = async (e) => {
    e.preventDefault();
    if ((!messageText.trim() && !selectedImageFile) || !selectedChat) return;

    setSendingMessage(true);
    try {
      let res;
      if (selectedImageFile) {
        res = await sendWhatsAppImage(
          selectedChat.phone_number,
          selectedImageFile,
          messageText.trim(),
          null,
          operatorName
        );
      } else {
        // Los mensajes libres de chat NO atan la conversación a un viaje antiguo por defecto
        res = await sendWhatsAppFreeText(selectedChat.phone_number, messageText, null, operatorName);
      }

      if (res.status === 'success') {
        setMessageText('');
        handleClearImage();
        if (selectedChat.pending_quote) {
          await resolveWhatsAppQuote(selectedChat.phone_number);
          setSelectedChat(prev => prev ? { ...prev, pending_quote: false } : null);
          setChats(prev => prev.map(c => c.phone_number === selectedChat.phone_number ? { ...c, pending_quote: false } : c));
          window.dispatchEvent(new CustomEvent('whatsapp_unread_updated'));
        }
        window.dispatchEvent(new CustomEvent('whatsapp_passenger_issue_cleared'));
        await fetchMessages(selectedChat.phone_number);
        fetchChats(false);
      } else {
        toast.error("Error al enviar mensaje: " + (res.error || "Error desconocido"));
      }
    } catch (error) {
      // Toast error handled by api wrapper
    } finally {
      setSendingMessage(false);
    }
  };

  // --- TRADUCTOR INSTANTÁNEO (ENTRANTE -> ESPAÑOL / SALIENTE -> IDIOMA PASAJERO) ---
  const handleTranslateIncomingMessage = async (msgId, rawText) => {
    if (!rawText || !rawText.trim()) return;
    if (messageTranslations[msgId]) {
      // Si ya estaba traducido, permitir ocultar/mostrar o volver a traducir
      setMessageTranslations(prev => {
        const copy = { ...prev };
        delete copy[msgId];
        return copy;
      });
      return;
    }
    try {
      setTranslatingMsgId(msgId);
      const res = await translateWhatsAppText(rawText, 'es', selectedChat?.phone_number || null);
      if (res && res.translated_text) {
        setMessageTranslations(prev => ({
          ...prev,
          [msgId]: {
            text: res.translated_text,
            sourceLang: res.detected_source_language || 'Idioma original'
          }
        }));
      }
    } catch (e) {
      // Error manejado por api wrapper
    } finally {
      setTranslatingMsgId(null);
    }
  };

  const handleTranslateOutboundDraft = async () => {
    if (!messageText || !messageText.trim()) {
      toast.info("Escribe primero tu respuesta en español para traducirla al idioma del pasajero.");
      return;
    }
    try {
      setTranslatingDraft(true);
      const res = await translateWhatsAppText(
        messageText.trim(),
        outboundTargetLang || 'auto_passenger',
        selectedChat?.phone_number || null
      );
      if (res && res.translated_text) {
        setMessageText(res.translated_text);
        toast.success(`Traducido a ${res.target_language_name || 'idioma del pasajero'}.`);
      }
    } catch (e) {
      // Error manejado por api wrapper
    } finally {
      setTranslatingDraft(false);
    }
  };

  // --- ACCIONES DE TICKETS (ESTILO WHATICKET) ---
  const handleAcceptChat = async (chatToAccept = null) => {
    const target = chatToAccept || selectedChat;
    if (!target) return;
    try {
      setProcessingTicketAction(true);
      const res = await acceptWhatsAppChat(target.phone_number, operatorName);
      if (res.status === 'success') {
        toast.success(`Conversación tomada por ${operatorName}.`);
        const nowIso = new Date().toISOString();
        const updatedChatFields = {
          ticket_status: 'IN_PROGRESS',
          assigned_to: operatorName,
          assigned_at: nowIso,
          last_read_by: operatorName,
          last_read_at: nowIso,
          requires_attention: false,
          unread_count: 0
        };

        setChats(prev => prev.map(c => c.phone_number === target.phone_number ? { ...c, ...updatedChatFields } : c));
        if (selectedChat?.phone_number === target.phone_number) {
          setSelectedChat(prev => prev ? { ...prev, ...updatedChatFields } : null);
          await fetchMessages(target.phone_number);
        }
        window.dispatchEvent(new CustomEvent('whatsapp_unread_updated'));
        window.dispatchEvent(new CustomEvent('whatsapp_passenger_issue_cleared'));
      }
    } catch (err) {
      toast.error("Error al tomar la conversación.");
    } finally {
      setProcessingTicketAction(false);
    }
  };

  const handleResolveChat = async (chatToResolve = null) => {
    const target = chatToResolve || selectedChat;
    if (!target) return;
    try {
      setProcessingTicketAction(true);
      const res = await resolveWhatsAppChat(target.phone_number, operatorName);
      if (res.status === 'success') {
        toast.success("Conversación resuelta y cerrada.");
        const nowIso = new Date().toISOString();
        const updatedChatFields = {
          ticket_status: 'RESOLVED',
          resolved_by: operatorName,
          resolved_at: nowIso,
          assigned_to: null,
          requires_attention: false,
          pending_quote: false,
          conversation_state: 'IDLE'
        };

        setChats(prev => prev.map(c => c.phone_number === target.phone_number ? { ...c, ...updatedChatFields } : c));
        if (selectedChat?.phone_number === target.phone_number) {
          setSelectedChat(prev => prev ? { ...prev, ...updatedChatFields } : null);
          await fetchMessages(target.phone_number);
        }
        window.dispatchEvent(new CustomEvent('whatsapp_unread_updated'));
        window.dispatchEvent(new CustomEvent('whatsapp_passenger_issue_cleared'));
      }
    } catch (err) {
      toast.error("Error al resolver la conversación.");
    } finally {
      setProcessingTicketAction(false);
    }
  };

  const handleResolveAllChats = async () => {
    if (!window.confirm("¿Deseas marcar TODOS los chats actuales como resueltos?")) return;
    try {
      setProcessingTicketAction(true);
      const res = await resolveAllWhatsAppChats(operatorName);
      if (res.status === 'success') {
        toast.success(`Se marcaron ${res.resolved_count} chats como resueltos.`);
        const nowIso = new Date().toISOString();
        setChats(prev => prev.map(c => ({
          ...c,
          ticket_status: 'RESOLVED',
          assigned_to: null,
          resolved_by: operatorName,
          resolved_at: nowIso,
          requires_attention: false,
          pending_quote: false,
          conversation_state: 'IDLE',
          unread_count: 0
        })));
        if (selectedChat) {
          setSelectedChat(prev => prev ? {
            ...prev,
            ticket_status: 'RESOLVED',
            assigned_to: null,
            resolved_by: operatorName,
            resolved_at: nowIso,
            requires_attention: false,
            pending_quote: false,
            conversation_state: 'IDLE',
            unread_count: 0
          } : null);
        }
        window.dispatchEvent(new CustomEvent('whatsapp_unread_updated'));
        window.dispatchEvent(new CustomEvent('whatsapp_passenger_issue_cleared'));
      }
    } catch (err) {
      toast.error("Error al resolver todos los chats.");
    } finally {
      setProcessingTicketAction(false);
    }
  };

  const handleReassignChat = async (targetUser = null) => {
    if (!selectedChat) return;
    try {
      setProcessingTicketAction(true);
      const res = await reassignWhatsAppChat(selectedChat.phone_number, operatorName, targetUser);
      if (res.status === 'success') {
        toast.info(targetUser ? `Conversación transferida a ${targetUser}.` : "Conversación devuelta a la cola de espera.");
        const isQueue = !targetUser || ["cola", "pending", "nadie"].includes(targetUser.toLowerCase());
        const updatedChatFields = {
          ticket_status: isQueue ? 'PENDING' : 'IN_PROGRESS',
          assigned_to: isQueue ? null : targetUser,
          assigned_at: isQueue ? null : new Date().toISOString()
        };

        setChats(prev => prev.map(c => c.phone_number === selectedChat.phone_number ? { ...c, ...updatedChatFields } : c));
        setSelectedChat(prev => prev ? { ...prev, ...updatedChatFields } : null);
        await fetchMessages(selectedChat.phone_number);
        window.dispatchEvent(new CustomEvent('whatsapp_unread_updated'));
      }
    } catch (err) {
      toast.error("Error al reasignar conversación.");
    } finally {
      setProcessingTicketAction(false);
    }
  };

  const handleResolveQuote = async () => {
    if (!selectedChat) return;
    try {
      await resolveWhatsAppQuote(selectedChat.phone_number);
      setSelectedChat(prev => prev ? { ...prev, pending_quote: false, requires_attention: false } : null);
      setChats(prev => prev.map(c => c.phone_number === selectedChat.phone_number ? { ...c, pending_quote: false, requires_attention: false } : c));
      window.dispatchEvent(new CustomEvent('whatsapp_unread_updated'));
      toast.success("Cotización marcada como atendida.");
    } catch (e) {
      toast.error("Error al actualizar cotización.");
    }
  };

  const handleMarkChatAsUnread = async (chatToMark = null) => {
    const target = chatToMark || selectedChat;
    if (!target) return;
    try {
      await markWhatsAppChatAsUnread(target.phone_number);
      // Actualizar estado local del chat
      setChats(prev => prev.map(c => {
        if (c.phone_number === target.phone_number) {
          return { ...c, unread_count: Math.max(1, (c.unread_count || 0) + 1), requires_attention: true };
        }
        return c;
      }));
      // Si el chat marcado es el que está actualmente abierto en el panel derecho, cerramos la selección
      // para que visualmente vuelva a aparecer como pendiente con su badge en la lista
      if (selectedChat?.phone_number === target.phone_number) {
        setSelectedChat(null);
        setMessages([]);
      }
      window.dispatchEvent(new CustomEvent('whatsapp_unread_updated'));
      toast.info(`Chat de ${target.contact_name || target.phone_number} marcado como no leído.`);
    } catch (err) {
      toast.error("Error al marcar chat como no leído.");
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!window.confirm("¿Deseas marcar todas las conversaciones históricas como leídas?")) return;
    try {
      setMarkingAllRead(true);
      await markAllWhatsAppAsRead();
      setChats(prev => prev.map(c => ({ ...c, unread_count: 0, requires_attention: false })));
      if (selectedChat) {
        setSelectedChat(prev => prev ? { ...prev, unread_count: 0, requires_attention: false } : null);
      }
      window.dispatchEvent(new CustomEvent('whatsapp_unread_updated'));
      toast.success("Todos los chats fueron marcados como leídos.");
    } catch (err) {
      toast.error("Error al marcar conversaciones como leídas.");
    } finally {
      setMarkingAllRead(false);
    }
  };

  // --- ABRIR MODAL DE INICIO DE CONVERSACIÓN (DESDE BOTÓN '+' O DESDE CHAT ABIERTO) ---
  const handleOpenNewConversationModal = () => {
    setInitModalMode('search');
    setContactSearchQuery('');
    setContactSearchResults([]);
    setTemplatePhone('');
    setTemplateVar1Name('');
    setTemplateVar2Message('');
    setShowInitTemplateModal(true);
  };

  const handleOpenChatTemplateModal = (chatObj = selectedChat) => {
    const target = chatObj || selectedChat;
    if (!target) {
      handleOpenNewConversationModal();
      return;
    }
    setInitModalMode('chat');
    setTemplatePhone(target.phone_number || '');
    const rawName = (target.contact_name || '').trim();
    const isPhoneName = !rawName || rawName === target.phone_number || /^\+?\d+$/.test(rawName);
    setTemplateVar1Name(isPhoneName ? '' : rawName);
    setTemplateVar2Message('');
    setShowInitTemplateModal(true);
  };

  useEffect(() => {
    if (!showInitTemplateModal || initModalMode !== 'search') return;
    const q = contactSearchQuery.trim();
    if (q.length < 2) {
      setContactSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchingContacts(true);
      try {
        const res = await searchWhatsAppContacts(q);
        setContactSearchResults(res || []);
      } catch (e) {
        setContactSearchResults([]);
      } finally {
        setSearchingContacts(false);
      }
    }, 280);
    return () => clearTimeout(timer);
  }, [contactSearchQuery, showInitTemplateModal, initModalMode]);

  const handleSelectContactFromSearch = (item) => {
    setTemplatePhone(item.phone_number || '');
    setTemplateVar1Name(item.contact_name || '');
    setContactSearchResults([]);
  };

  // --- ABRIR CONVERSACIÓN HISTÓRICA EN MODO LECTURA (SIN ENVIAR PLANTILLA NI MENSAJE) ---
  const handleOpenHistorySearchModal = () => {
    setHistorySearchQuery('');
    setHistorySearchResults([]);
    setShowHistorySearchModal(true);
  };

  const handleOpenReadOnlyChat = (rawItem) => {
    if (!rawItem || !rawItem.phone_number) return;
    const rawPhoneStr = String(rawItem.phone_number).trim();
    const isBooking = rawPhoneStr.toLowerCase().startsWith('booking:') || rawItem.channel === 'booking_chat' || rawItem.contact_type === 'booking_live_chat';
    const normalizedPhone = isBooking
      ? `booking:${rawPhoneStr.replace(/^booking:/i, '').trim()}`
      : rawPhoneStr.replace(/^\+/, '');

    // Si ya existe en los chats cargados o en resultados remotos, reutilizar su metadata completa
    const existingMatch =
      chats.find(c => String(c.phone_number) === normalizedPhone || (!isBooking && normalizedPhone.length >= 8 && String(c.phone_number || '').endsWith(normalizedPhone.slice(-8)))) ||
      (remoteSearchChats || []).find(c => String(c.phone_number) === normalizedPhone) ||
      (historySearchResults || []).find(c => String(c.phone_number) === normalizedPhone);

    const chatToOpen = existingMatch
      ? {
          ...existingMatch,
          unread_count: 0,
          requires_attention: false
        }
      : {
          phone_number: normalizedPhone,
          channel: isBooking ? 'booking_chat' : 'whatsapp',
          booking_reference: isBooking ? normalizedPhone.split(':')[1] : (rawItem.booking_reference || null),
          contact_name: rawItem.contact_name || (isBooking ? `🏨 Pasajero Booking #${normalizedPhone.split(':')[1]}` : `+${normalizedPhone}`),
          contact_type: isBooking ? 'booking_live_chat' : (rawItem.contact_type || 'passenger'),
          last_message_at: rawItem.last_message_at || null,
          unread_count: 0,
          requires_attention: false,
          pending_quote: false,
          ticket_status: rawItem.ticket_status || 'RESOLVED',
          assigned_to: rawItem.assigned_to || null,
          is_session_active: isBooking ? true : Boolean(rawItem.is_session_active),
          last_trip_id: rawItem.last_trip_id || null,
          last_message_preview: rawItem.last_message_preview || '',
          last_message_direction: rawItem.last_message_direction || '',
          conversation_state: rawItem.conversation_state || 'IDLE',
          conversation_context: rawItem.conversation_context || {}
        };

    setShowHistorySearchModal(false);
    setShowInitTemplateModal(false);
    setChats(prev => {
      const exists = prev.some(c => c.phone_number === chatToOpen.phone_number);
      if (exists) {
        return prev.map(c => c.phone_number === chatToOpen.phone_number ? chatToOpen : c);
      }
      return [chatToOpen, ...prev];
    });
    setSelectedChat(chatToOpen);
  };

  // Búsqueda global en barra lateral: consulta toda la BD cuando hay >= 2 caracteres
  useEffect(() => {
    const q = searchTerm.trim().replace(/^#/, '');
    if (q.length < 2) {
      setRemoteSearchChats(null);
      setSearchingRemoteChats(false);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearchingRemoteChats(true);
      try {
        const res = await getWhatsAppChats(60, 0, q);
        if (!cancelled) {
          setRemoteSearchChats(Array.isArray(res) ? res : []);
        }
      } catch (err) {
        if (!cancelled) {
          setRemoteSearchChats([]);
        }
      } finally {
        if (!cancelled) {
          setSearchingRemoteChats(false);
        }
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Búsqueda dedicada dentro del modal "Buscar Chat / Leer Historial"
  useEffect(() => {
    if (!showHistorySearchModal) return;
    const q = historySearchQuery.trim().replace(/^#/, '');
    if (q.length < 2) {
      setHistorySearchResults([]);
      setSearchingHistoryModal(false);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearchingHistoryModal(true);
      try {
        const res = await getWhatsAppChats(50, 0, q);
        if (!cancelled) {
          setHistorySearchResults(Array.isArray(res) ? res : []);
        }
      } catch (err) {
        if (!cancelled) {
          setHistorySearchResults([]);
        }
      } finally {
        if (!cancelled) {
          setSearchingHistoryModal(false);
        }
      }
    }, 280);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [historySearchQuery, showHistorySearchModal]);

  const handleSendInitTemplate = async (e) => {
    if (e) e.preventDefault();
    const phoneToUse = (templatePhone || contactSearchQuery || '').trim();
    if (!phoneToUse || phoneToUse.replace(/\D/g, '').length < 8) {
      toast.warning("Por favor selecciona un pasajero o ingresa un número de teléfono válido.");
      return;
    }
    if (!templateVar2Message.trim()) {
      toast.warning("Por favor escribe el mensaje o motivo (Variable 2) para enviar la plantilla.");
      return;
    }
    const var1 = templateVar1Name.trim() || "Estimado(a)";
    const var2 = templateVar2Message.trim();

    setSendingInitTemplate(true);
    try {
      const res = await sendWhatsAppTemplate(
        phoneToUse,
        "inicio_conversacion_emilia",
        [var1, var2],
        selectedChat?.last_trip_id || null,
        "es",
        operatorName
      );
      if (res && res.status === 'success') {
        toast.success(`Plantilla enviada a ${var1} (${phoneToUse}).`);
        setShowInitTemplateModal(false);
        await fetchChats(false);
        // Seleccionar y abrir automáticamente el chat recién iniciado/actualizado
        const digits = phoneToUse.replace(/\D/g, '');
        const last8 = digits.slice(-8);
        const updatedChats = await getWhatsAppChats(100, 0);
        if (updatedChats && Array.isArray(updatedChats)) {
          setChats(updatedChats);
          const matched = updatedChats.find(c => (c.phone_number || '').replace(/\D/g, '').endsWith(last8));
          if (matched) {
            setSelectedChat(matched);
            await fetchMessages(matched.phone_number, false);
          }
        }
      } else {
        toast.error("No se pudo enviar la plantilla.");
      }
    } catch (err) {
      // Manejado por wrapper API
    } finally {
      setSendingInitTemplate(false);
    }
  };

  const formatChatListDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    const yesterday = new Date();
    yesterday.setDate(now.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) {
      return 'Ayer';
    }
    return d.toLocaleDateString('es-CL', { day: 'numeric', month: 'short' });
  };

  const pendingQuotesCount = chats.filter(c => c.pending_quote).length;
  const attentionCount = chats.filter(c => c.requires_attention || c.conversation_state === 'TRANSFERRED_LOCUTORA').length;
  
  // Contadores para pestañas Whaticket
  const queueCount = chats.filter(c => (!c.ticket_status || c.ticket_status === 'PENDING') && c.ticket_status !== 'RESOLVED').length;
  const inProgressTotalCount = chats.filter(c => c.ticket_status === 'IN_PROGRESS').length;
  const myChatsCount = chats.filter(c => c.ticket_status === 'IN_PROGRESS' && c.assigned_to && c.assigned_to.toLowerCase() === operatorName.toLowerCase()).length;
  const resolvedCount = chats.filter(c => c.ticket_status === 'RESOLVED').length;
  const bookingChatCount = chats.filter(c => isBookingChatItem(c)).length;
  const bookingChatUnreadCount = chats.filter(c => isBookingChatItem(c) && (c.unread_count > 0 || c.requires_attention)).length;
  const whatsappChatCount = chats.length - bookingChatCount;

  const isGlobalSearchActive = searchTerm.trim().replace(/^#/, '').length >= 2;

  const filteredChats = (() => {
    if (isGlobalSearchActive) {
      const term = searchTerm.trim().replace(/^#/, '').toLowerCase();
      const mergedByPhone = new Map();
      // 1. Coincidencias locales inmediatas
      chats.forEach(c => {
        const name = (c.contact_name || '').toLowerCase();
        const phone = (c.phone_number || '').toLowerCase();
        const bookingRef = (c.booking_reference || '').toLowerCase();
        const tripId = c.last_trip_id ? c.last_trip_id.toString() : '';
        if (name.includes(term) || phone.includes(term) || bookingRef.includes(term) || tripId.includes(term)) {
          mergedByPhone.set(c.phone_number, c);
        }
      });
      // 2. Coincidencias históricas globales desde PostgreSQL (incluye chats >100, resueltos y cruces con viajes)
      if (Array.isArray(remoteSearchChats)) {
        remoteSearchChats.forEach(rc => {
          if (!mergedByPhone.has(rc.phone_number)) {
            mergedByPhone.set(rc.phone_number, rc);
          } else {
            const prev = mergedByPhone.get(rc.phone_number);
            mergedByPhone.set(rc.phone_number, {
              ...prev,
              last_trip_id: prev.last_trip_id || rc.last_trip_id,
              last_message_preview: prev.last_message_preview || rc.last_message_preview
            });
          }
        });
      }
      return Array.from(mergedByPhone.values())
        .filter(c => {
          if (channelFilter === 'booking_chat' && !isBookingChatItem(c)) return false;
          if (channelFilter === 'whatsapp' && isBookingChatItem(c)) return false;
          return true;
        })
        .sort((a, b) => new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0));
    }

    return chats
      .filter(c => {
        const term = searchTerm.toLowerCase();
        if (term) {
          const name = (c.contact_name || '').toLowerCase();
          const phone = (c.phone_number || '').toLowerCase();
          const bookingRef = (c.booking_reference || '').toLowerCase();
          const tripId = c.last_trip_id ? c.last_trip_id.toString() : '';
          if (!(name.includes(term) || phone.includes(term) || bookingRef.includes(term) || tripId.includes(term))) {
            return false;
          }
        }

        // Filtro por Canal (Todos vs WhatsApp vs Booking Live Chat)
        if (channelFilter === 'booking_chat' && !isBookingChatItem(c)) return false;
        if (channelFilter === 'whatsapp' && isBookingChatItem(c)) return false;

        // Filtro de Pestaña principal estilo Whaticket
        if (tabFilter === 'pending') {
          const isPending = (!c.ticket_status || c.ticket_status === 'PENDING') && c.ticket_status !== 'RESOLVED';
          if (!isPending) return false;
        } else if (tabFilter === 'in_progress') {
          if (c.ticket_status !== 'IN_PROGRESS') return false;
        } else if (tabFilter === 'mine') {
          const isMine = c.ticket_status === 'IN_PROGRESS' && c.assigned_to && c.assigned_to.toLowerCase() === operatorName.toLowerCase();
          if (!isMine) return false;
        } else if (tabFilter === 'resolved') {
          if (c.ticket_status !== 'RESOLVED') return false;
        }
        // Si tabFilter === 'all', no filtra por estado de ticket

        if (filterAttentionOnly) {
          const needsAttention = c.requires_attention || c.conversation_state === 'TRANSFERRED_LOCUTORA';
          if (!needsAttention) return false;
        }

        if (filterQuotesOnly) {
          if (!c.pending_quote) return false;
        }

        if (filterUnreadOnly) {
          const isUnread = (c.unread_count > 0 || c.requires_attention);
          if (!isUnread) return false;
        }
        
        if (filterTodayOnly) {
          if (!c.last_message_at) return false;
          const d = new Date(c.last_message_at);
          const now = new Date();
          return d.toDateString() === now.toDateString();
        }
        return true;
      })
      .sort((a, b) => new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0));
  })();

  const getStatusIcon = (status) => {
    switch(status) {
      case 'sent':
        return <Check size={14} className="text-muted" title="Enviado a Meta" />;
      case 'delivered':
        return <CheckCheck size={14} className="text-muted" title="Entregado al celular" />;
      case 'read':
        return <CheckCheck size={14} className="text-info" title="Leído por el destinatario" />;
      case 'failed':
        return <AlertCircle size={14} className="text-danger" title="Fallo de entrega" />;
      default:
        return null;
    }
  };

  const getMessageDateLabel = (dateStr) => {
    if (!dateStr) return '';
    const msgDate = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const isSameDay = (d1, d2) => 
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate();

    if (isSameDay(msgDate, today)) {
      return 'Hoy';
    } else if (isSameDay(msgDate, yesterday)) {
      return 'Ayer';
    } else {
      return msgDate.toLocaleDateString('es-CL', {
        day: 'numeric',
        month: 'long',
        year: msgDate.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  return (
    <div className="whatsapp-chat-center rounded-3 overflow-hidden shadow" style={{ height: 'calc(100vh - 200px)', display: 'flex', backgroundColor: '#0f172a', border: '1px solid #1e293b' }}>
      
      {/* 1. SIDEBAR - CHATS LIST */}
      <div className="border-end" style={{ width: '385px', minWidth: '385px', display: 'flex', flexDirection: 'column', backgroundColor: '#0b1120', borderColor: '#1e293b' }}>
        
        {/* Header Search & Refresh & Filtros */}
        <div className="p-3 border-bottom d-flex flex-column gap-2" style={{ backgroundColor: '#0f172a', borderColor: '#1e293b' }}>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0 text-white fw-bold d-flex align-items-center gap-2" style={{ fontSize: '1rem' }}>
              <MessageSquare size={18} style={{ color: '#a855f7' }} /> Conversaciones
            </h5>
            <div className="d-flex gap-1 align-items-center flex-wrap justify-content-end">
              <Button 
                variant={filterAttentionOnly ? "danger" : "outline-secondary"} 
                size="sm" 
                className="py-0 px-2 fw-medium" 
                style={filterAttentionOnly ? { backgroundColor: '#dc2626', borderColor: '#dc2626', color: '#fff', fontSize: '0.72rem' } : (attentionCount > 0 ? { fontSize: '0.72rem', borderColor: '#ef4444', color: '#f87171' } : { fontSize: '0.72rem', borderColor: '#334155', color: '#94a3b8' })}
                onClick={() => setFilterAttentionOnly(!filterAttentionOnly)}
                title="Mostrar sólo conversaciones que requieren atención de locutora o transferidas por IA"
              >
                🚨 {attentionCount > 0 ? `Locutora (${attentionCount})` : 'Locutora'}
              </Button>
              <Button 
                variant={filterQuotesOnly ? "warning" : "outline-secondary"} 
                size="sm" 
                className="py-0 px-2 fw-medium" 
                style={filterQuotesOnly ? { backgroundColor: '#f59e0b', borderColor: '#f59e0b', color: '#000', fontSize: '0.72rem' } : (pendingQuotesCount > 0 ? { fontSize: '0.72rem', borderColor: '#f59e0b', color: '#f59e0b' } : { fontSize: '0.72rem', borderColor: '#334155', color: '#94a3b8' })}
                onClick={() => setFilterQuotesOnly(!filterQuotesOnly)}
                title="Mostrar sólo conversaciones con solicitudes de cotización pendientes"
              >
                ⚡ {pendingQuotesCount > 0 ? `Cotizar (${pendingQuotesCount})` : 'Cotizar'}
              </Button>
              <Button 
                variant={filterUnreadOnly ? "danger" : "outline-secondary"} 
                size="sm" 
                className="py-0 px-2 fw-medium" 
                style={filterUnreadOnly ? { backgroundColor: '#ef4444', borderColor: '#ef4444', color: '#fff', fontSize: '0.72rem' } : { fontSize: '0.72rem', borderColor: '#334155', color: '#94a3b8' }}
                onClick={() => setFilterUnreadOnly(!filterUnreadOnly)}
                title="Mostrar sólo conversaciones con respuestas pendientes de pasajeros"
              >
                🔔 No leídos
              </Button>
              <Button 
                variant={filterTodayOnly ? "primary" : "outline-secondary"} 
                size="sm" 
                className="py-0 px-2 fw-medium" 
                style={filterTodayOnly ? { backgroundColor: '#8b5cf6', borderColor: '#8b5cf6', fontSize: '0.72rem' } : { fontSize: '0.72rem', borderColor: '#334155', color: '#94a3b8' }}
                onClick={() => setFilterTodayOnly(!filterTodayOnly)}
                title="Filtrar solo actividad de hoy"
              >
                📅 Hoy
              </Button>
              <Button 
                variant="outline-secondary" 
                size="sm" 
                className="p-1" 
                style={{ borderColor: '#334155', color: '#94a3b8' }} 
                onClick={handleMarkAllAsRead} 
                disabled={markingAllRead}
                title="Marcar todas las conversaciones como leídas"
              >
                <CheckAll size={14} className={markingAllRead ? "animate-spin" : ""} />
              </Button>
              <Button 
                variant="outline-secondary" 
                size="sm" 
                className="py-0 px-2 fw-medium d-flex align-items-center gap-1" 
                style={{ fontSize: '0.72rem', borderColor: '#334155', color: '#94a3b8' }}
                onClick={handleResolveAllChats}
                disabled={processingTicketAction}
                title="Marcar todas las conversaciones no resueltas como resueltas"
              >
                <CheckCircle2 size={12} className="text-success" />
                <span>Resolver todos</span>
              </Button>
              <Button 
                variant="outline-secondary" 
                size="sm" 
                className="p-1" 
                style={{ borderColor: '#334155', color: '#94a3b8' }} 
                onClick={() => fetchChats(true)} 
                disabled={loadingChats}
                title="Actualizar lista"
              >
                <RefreshCw size={14} className={loadingChats ? 'animate-spin text-purple' : ''} />
              </Button>
            </div>
          </div>

          {/* Pestañas de estado estilo Whaticket según Rol (4 columnas visibles sin desborde) */}
          <div className="d-flex p-1 rounded-2 align-items-stretch" style={{ backgroundColor: '#1e293b', gap: '3px' }}>
            {isSupervisor ? (
              <>
                <button
                  type="button"
                  className={`btn btn-sm flex-fill py-1 px-1 d-flex align-items-center justify-content-center gap-1 border-0 fw-semibold`}
                  style={{ fontSize: '0.68rem', whiteSpace: 'nowrap', borderRadius: '4px', backgroundColor: tabFilter === 'in_progress' ? '#1d4ed8' : 'transparent', color: tabFilter === 'in_progress' ? '#ffffff' : '#cbd5e1' }}
                  onClick={() => setTabFilter('in_progress')}
                  title="Todos los chats tomados por operadoras"
                >
                  <UserCheck size={11} className="flex-shrink-0" />
                  <span>Atención</span>
                  {inProgressTotalCount > 0 && (
                    <span className="badge rounded-pill text-white" style={{ fontSize: '0.6rem', padding: '2px 5px', backgroundColor: '#22c55e' }}>{inProgressTotalCount}</span>
                  )}
                </button>
                <button
                  type="button"
                  className={`btn btn-sm flex-fill py-1 px-1 d-flex align-items-center justify-content-center gap-1 border-0 fw-semibold`}
                  style={{ fontSize: '0.68rem', whiteSpace: 'nowrap', borderRadius: '4px', backgroundColor: tabFilter === 'pending' ? '#7c3aed' : 'transparent', color: tabFilter === 'pending' ? '#ffffff' : '#cbd5e1' }}
                  onClick={() => setTabFilter('pending')}
                  title="Chats en cola o interactuando con la IA"
                >
                  <Clock size={11} className="flex-shrink-0" />
                  <span>Espera</span>
                  {queueCount > 0 && (
                    <span className="badge rounded-pill text-white" style={{ fontSize: '0.6rem', padding: '2px 5px', backgroundColor: '#22c55e' }}>{queueCount}</span>
                  )}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className={`btn btn-sm flex-fill py-1 px-1 d-flex align-items-center justify-content-center gap-1 border-0 fw-semibold`}
                  style={{ fontSize: '0.68rem', whiteSpace: 'nowrap', borderRadius: '4px', backgroundColor: tabFilter === 'pending' ? '#7c3aed' : 'transparent', color: tabFilter === 'pending' ? '#ffffff' : '#cbd5e1' }}
                  onClick={() => setTabFilter('pending')}
                  title="Chats en cola o interactuando con la IA"
                >
                  <Clock size={11} className="flex-shrink-0" />
                  <span>Espera</span>
                  {queueCount > 0 && (
                    <span className="badge rounded-pill text-white" style={{ fontSize: '0.6rem', padding: '2px 5px', backgroundColor: '#22c55e' }}>{queueCount}</span>
                  )}
                </button>
                <button
                  type="button"
                  className={`btn btn-sm flex-fill py-1 px-1 d-flex align-items-center justify-content-center gap-1 border-0 fw-semibold`}
                  style={{ fontSize: '0.68rem', whiteSpace: 'nowrap', borderRadius: '4px', backgroundColor: tabFilter === 'mine' ? '#0284c7' : 'transparent', color: tabFilter === 'mine' ? '#ffffff' : '#cbd5e1' }}
                  onClick={() => setTabFilter('mine')}
                  title={`Mis chats asignados (${operatorName})`}
                >
                  <UserCheck size={11} className="flex-shrink-0" />
                  <span>Mis Chats</span>
                  {myChatsCount > 0 && (
                    <span className="badge rounded-pill text-white" style={{ fontSize: '0.6rem', padding: '2px 5px', backgroundColor: '#22c55e' }}>{myChatsCount}</span>
                  )}
                </button>
              </>
            )}
            <button
              type="button"
              className={`btn btn-sm flex-fill py-1 px-1 d-flex align-items-center justify-content-center gap-1 border-0 fw-semibold`}
              style={{ fontSize: '0.68rem', whiteSpace: 'nowrap', borderRadius: '4px', backgroundColor: tabFilter === 'resolved' ? '#16a34a' : 'transparent', color: tabFilter === 'resolved' ? '#ffffff' : '#cbd5e1' }}
              onClick={() => setTabFilter('resolved')}
              title="Conversaciones resueltas y archivadas"
            >
              <CheckCircle2 size={11} className="flex-shrink-0" />
              <span>Resueltos</span>
              {resolvedCount > 0 && (
                <span className="badge rounded-pill bg-dark text-white" style={{ fontSize: '0.6rem', padding: '2px 5px' }}>{resolvedCount}</span>
              )}
            </button>
            <button
              type="button"
              className={`btn btn-sm flex-fill py-1 px-1 d-flex align-items-center justify-content-center gap-1 border-0 fw-semibold`}
              style={{ fontSize: '0.68rem', whiteSpace: 'nowrap', borderRadius: '4px', backgroundColor: tabFilter === 'all' ? '#4f46e5' : 'rgba(255,255,255,0.04)', color: tabFilter === 'all' ? '#ffffff' : '#e2e8f0' }}
              onClick={() => setTabFilter('all')}
              title="Todas las conversaciones"
            >
              <span>Todos</span>
              {chats.length > 0 && (
                <span className="badge rounded-pill bg-secondary text-white" style={{ fontSize: '0.58rem', padding: '2px 4px' }}>{chats.length}</span>
              )}
            </button>
          </div>

          {/* Selector de Canal: distinción clara entre WhatsApp y Booking Live Chat */}
          <div className="d-flex p-1 rounded-2 align-items-stretch" style={{ backgroundColor: '#111827', border: '1px solid #1e293b', gap: '4px' }}>
            <button
              type="button"
              className="btn btn-sm flex-fill py-1 px-2 d-flex align-items-center justify-content-center gap-1 border-0 fw-bold"
              style={{
                fontSize: '0.68rem',
                borderRadius: '4px',
                backgroundColor: channelFilter === 'all' ? '#334155' : 'transparent',
                color: channelFilter === 'all' ? '#ffffff' : '#94a3b8'
              }}
              onClick={() => setChannelFilter('all')}
              title="Ver todos los canales (WhatsApp + Booking Live Chat)"
            >
              <span>🌐 Todos ({chats.length})</span>
            </button>
            <button
              type="button"
              className="btn btn-sm flex-fill py-1 px-2 d-flex align-items-center justify-content-center gap-1 border-0 fw-bold"
              style={{
                fontSize: '0.68rem',
                borderRadius: '4px',
                backgroundColor: channelFilter === 'whatsapp' ? '#15803d' : 'transparent',
                color: channelFilter === 'whatsapp' ? '#ffffff' : '#4ade80'
              }}
              onClick={() => setChannelFilter('whatsapp')}
              title="Filtrar solo conversaciones de WhatsApp"
            >
              <span>📱 WhatsApp ({whatsappChatCount})</span>
            </button>
            <button
              type="button"
              className="btn btn-sm flex-fill py-1 px-2 d-flex align-items-center justify-content-center gap-1 border-0 fw-bold"
              style={{
                fontSize: '0.68rem',
                borderRadius: '4px',
                backgroundColor: channelFilter === 'booking_chat' ? '#003580' : 'rgba(0, 53, 128, 0.25)',
                color: channelFilter === 'booking_chat' ? '#ffffff' : '#60a5fa',
                border: bookingChatUnreadCount > 0 ? '1px solid #3b82f6' : 'none'
              }}
              onClick={() => setChannelFilter('booking_chat')}
              title="Filtrar solo conversaciones del Live Chat oficial de Booking.com"
            >
              <span>🏨 Booking Chat ({bookingChatCount})</span>
              {bookingChatUnreadCount > 0 && (
                <span className="badge rounded-pill bg-danger text-white" style={{ fontSize: '0.58rem', padding: '2px 5px' }}>
                  {bookingChatUnreadCount}
                </span>
              )}
            </button>
          </div>
          
          <InputGroup size="sm">
            <InputGroup.Text style={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#94a3b8' }}>
              <Search size={14} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              name="sidebar_chat_search"
              placeholder="Buscar en todo el historial (nombre, fono, #viaje, Booking)..."
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              enterKeyHint="search"
              aria-label="Buscar conversación en todo el historial por nombre, teléfono, número de viaje o código Booking"
              style={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#ffffff' }}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            {searchingRemoteChats && (
              <InputGroup.Text style={{ backgroundColor: '#1e293b', borderColor: '#334155' }}>
                <Spinner animation="border" size="sm" style={{ color: '#a855f7', width: '13px', height: '13px' }} />
              </InputGroup.Text>
            )}
            {searchTerm && (
              <Button
                variant="outline-secondary"
                size="sm"
                style={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#cbd5e1' }}
                title="Limpiar búsqueda"
                onClick={() => setSearchTerm('')}
              >
                <X size={13} />
              </Button>
            )}
          </InputGroup>
          {isGlobalSearchActive && (
            <div
              aria-live="polite"
              className="d-flex justify-content-between align-items-center px-2 py-1 rounded-2"
              style={{ backgroundColor: 'rgba(14, 165, 233, 0.14)', border: '1px solid rgba(56, 189, 248, 0.35)', fontSize: '0.68rem', color: '#7dd3fc' }}
            >
              <span>
                🔍 Historial completo: <strong>{filteredChats.length}</strong> conversación(es)
              </span>
              <span className="text-info opacity-75">Solo lectura al abrir</span>
            </div>
          )}
        </div>

        {/* Chats scroll list */}
        <div className="overflow-auto flex-grow-1">
          {loadingChats && chats.length === 0 ? (
            <div className="text-center p-4">
              <Spinner animation="border" size="sm" style={{ color: '#a855f7' }} />
              <p className="small text-muted mt-2">Cargando chats...</p>
            </div>
          ) : filteredChats.length === 0 ? (
            <div className="text-center p-4 small" style={{ color: '#64748b' }}>
              {searchingRemoteChats
                ? "Buscando en todo el historial de mensajes..."
                : filterUnreadOnly && !isGlobalSearchActive
                  ? "No hay mensajes de pasajeros pendientes por responder." 
                  : filterTodayOnly && !isGlobalSearchActive
                    ? "No hubo actividad de mensajes hoy." 
                    : "No se encontraron conversaciones."}
            </div>
          ) : (
            <div className="d-flex flex-column">
              {filteredChats.map(c => {
                const isSelected = selectedChat?.phone_number === c.phone_number;
                const isBookingChat = isBookingChatItem(c);
                const isTransfeeroChat = isTransfeeroChatItem(c);
                const formattedTime = formatChatListDate(c.last_message_at);
                const isOutbound = c.last_message_direction === 'outbound';
                const isPendingQueue = (!c.ticket_status || c.ticket_status === 'PENDING') && c.ticket_status !== 'RESOLVED';
                const contactInitial = isBookingChat ? '🏨' : (isTransfeeroChat ? '🚘' : getContactInitial(c.contact_name, c.phone_number));
                const avatarColor = isBookingChat ? '#003580' : (isTransfeeroChat ? '#065f46' : getAvatarColor(c.contact_name || c.phone_number));
                const assignedInitial = c.assigned_to ? c.assigned_to.trim().charAt(0).toUpperCase() : null;

                return (
                  <div 
                    key={c.phone_number}
                    className="d-flex align-items-stretch cursor-pointer"
                    style={{
                      backgroundColor: isSelected
                        ? (isBookingChat ? '#0c2d5e' : (isTransfeeroChat ? '#064e3b' : '#2e1065'))
                        : (isBookingChat ? 'rgba(0, 53, 128, 0.14)' : (isTransfeeroChat ? 'rgba(5, 150, 105, 0.14)' : 'transparent')),
                      borderLeft: isSelected
                        ? (isBookingChat ? '4px solid #3b82f6' : (isTransfeeroChat ? '4px solid #10b981' : '4px solid #a855f7'))
                        : (isBookingChat ? '4px solid #003580' : (isTransfeeroChat ? '4px solid #059669' : '4px solid transparent')),
                      borderBottom: '1px solid #1e293b',
                      color: '#ffffff',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = isBookingChat ? 'rgba(0, 53, 128, 0.25)' : (isTransfeeroChat ? 'rgba(5, 150, 105, 0.24)' : '#131c2e');
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = isBookingChat ? 'rgba(0, 53, 128, 0.14)' : (isTransfeeroChat ? 'rgba(5, 150, 105, 0.14)' : 'transparent');
                    }}
                    onClick={() => {
                      const wasUnread = (c.unread_count > 0 || c.requires_attention);
                      const updatedChat = { 
                        ...c, 
                        unread_count: 0, 
                        requires_attention: false,
                        last_read_by: wasUnread && operatorName ? operatorName : (c.last_read_by || operatorName),
                        last_read_at: wasUnread ? new Date().toISOString() : (c.last_read_at || new Date().toISOString())
                      };
                      setSelectedChat(updatedChat);
                      setChats(prev => prev.map(item => {
                        if (item.phone_number === c.phone_number) {
                          return updatedChat;
                        }
                        return item;
                      }));
                      window.dispatchEvent(new CustomEvent('whatsapp_unread_updated'));
                    }}
                  >
                    {/* Contenido principal del Chat (Click para espiar/abrir conversación) */}
                    <div className="d-flex gap-2 p-2 flex-grow-1 overflow-hidden align-items-start">
                      {/* Avatar del Contacto con Badge de Operadora en esquina */}
                      <div className="position-relative flex-shrink-0" style={{ width: '42px', height: '42px' }}>
                        <div 
                          className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold shadow-sm"
                          style={{ 
                            width: '42px', 
                            height: '42px', 
                            backgroundColor: avatarColor,
                            border: isBookingChat ? '2px solid #3b82f6' : (isTransfeeroChat ? '2px solid #10b981' : 'none'),
                            fontSize: '1rem',
                            userSelect: 'none'
                          }}
                        >
                          {contactInitial}
                        </div>
                        {/* Badge flotante verde con inicial de la operadora si está en atención */}
                        {c.ticket_status === 'IN_PROGRESS' && assignedInitial && (
                          <div 
                            className="position-absolute rounded-circle d-flex align-items-center justify-content-center text-white shadow"
                            style={{ 
                              bottom: '-2px', 
                              right: '-2px', 
                              width: '18px', 
                              height: '18px', 
                              backgroundColor: '#22c55e', 
                              border: '2px solid #0b1120',
                              fontSize: '0.62rem',
                              fontWeight: '800',
                              lineHeight: 1
                            }}
                            title={`Atendido por: ${c.assigned_to}`}
                          >
                            {assignedInitial}
                          </div>
                        )}
                      </div>

                      {/* Datos del Chat */}
                      <div className="d-flex flex-column flex-grow-1 overflow-hidden" style={{ minWidth: 0 }}>
                        {/* Fila 1: Nombre + Hora + Botón no leído */}
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fw-bold text-white text-truncate" style={{ fontSize: '0.86rem', maxWidth: isPendingQueue ? '125px' : '175px' }}>
                            {c.contact_name || c.phone_number}
                          </span>
                          <div className="d-flex align-items-center gap-1 flex-shrink-0">
                            <span 
                              className="small" 
                              style={{ fontSize: '0.7rem', color: formattedTime.includes(':') ? (isBookingChat ? '#60a5fa' : (isTransfeeroChat ? '#34d399' : '#c084fc')) : '#64748b' }}
                            >
                              {formattedTime}
                            </span>
                            <button
                              type="button"
                              className="btn btn-link p-0 text-secondary"
                              style={{ color: '#94a3b8', lineHeight: 1 }}
                              title="Marcar como no leído"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkChatAsUnread(c);
                              }}
                            >
                              <Mail size={12} className="hover-text-info" />
                            </button>
                          </div>
                        </div>

                        {/* Fila 2: Último mensaje + Íconos y Contador de no leídos */}
                        <div className="d-flex justify-content-between align-items-center mt-1">
                          <span className="small text-truncate" style={{ fontSize: '0.78rem', color: isSelected ? '#e2e8f0' : '#94a3b8', maxWidth: isPendingQueue ? '130px' : '185px' }}>
                            {c.last_message_preview ? (
                              <>
                                {isOutbound ? (
                                  <span className="text-secondary fw-semibold">
                                    {c.assigned_to ? `${c.assigned_to}: ` : 'Tú: '}
                                  </span>
                                ) : null}
                                {c.last_message_preview}
                              </>
                            ) : (
                              <span className="text-muted italic">
                                {isBookingChat
                                  ? `Booking #${c.booking_reference || String(c.phone_number || '').replace('booking:', '')}`
                                  : isTransfeeroChat
                                    ? `Transfeero #${c.booking_reference || String(c.phone_number || '').replace('transfeero:', '')}`
                                    : (c.phone_number?.startsWith('+') ? c.phone_number : `+${c.phone_number}`)}
                              </span>
                            )}
                          </span>
                          <div className="d-flex gap-1 align-items-center flex-shrink-0">
                            {isBookingChat ? (
                              <span style={{ color: '#60a5fa', fontSize: '0.75rem', lineHeight: 1 }} title="Booking.com Live Chat">🏨</span>
                            ) : isTransfeeroChat ? (
                              <span style={{ color: '#34d399', fontSize: '0.75rem', lineHeight: 1 }} title="Alerta Operativa Transfeero">🚘</span>
                            ) : (
                              <span style={{ color: '#22c55e', fontSize: '0.75rem', lineHeight: 1 }} title="WhatsApp">📱</span>
                            )}
                            {c.last_read_by && (
                              <Eye size={12} style={{ color: '#94a3b8' }} title={`Visto por ${c.last_read_by}`} />
                            )}
                            {c.unread_count > 0 && (
                              <span 
                                className="badge rounded-circle text-white d-flex align-items-center justify-content-center shadow-sm"
                                style={{ 
                                  backgroundColor: isBookingChat ? '#2563eb' : (isTransfeeroChat ? '#10b981' : '#22c55e'), 
                                  minWidth: '18px', 
                                  height: '18px', 
                                  fontSize: '0.65rem',
                                  padding: '0 4px',
                                  fontWeight: '700'
                                }}
                              >
                                {c.unread_count}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Fila 3: Badges de Canal (WhatsApp vs Booking Live Chat) + Estado / Asignación */}
                        <div className="d-flex justify-content-between align-items-center mt-1 gap-1 flex-wrap">
                          <div className="d-flex align-items-center gap-1 flex-wrap">
                            {isBookingChat ? (
                              <span
                                className="badge fw-bold text-uppercase d-inline-flex align-items-center gap-1"
                                style={{
                                  backgroundColor: '#003580',
                                  color: '#ffffff',
                                  border: '1px solid #3b82f6',
                                  fontSize: '0.58rem',
                                  padding: '2px 5px',
                                  letterSpacing: '0.02em'
                                }}
                                title="Mensaje por Booking.com Live Chat"
                              >
                                🏨 BOOKING CHAT
                              </span>
                            ) : (
                              <span
                                className="badge fw-bold text-uppercase d-inline-flex align-items-center gap-1"
                                style={{
                                  backgroundColor: 'rgba(22, 163, 74, 0.2)',
                                  color: '#4ade80',
                                  border: '1px solid rgba(34, 197, 94, 0.45)',
                                  fontSize: '0.56rem',
                                  padding: '2px 5px'
                                }}
                                title="Mensaje por WhatsApp"
                              >
                                📱 WHATSAPP
                              </span>
                            )}
                            {!isBookingChat && (
                              <Badge bg={c.contact_type === 'driver' ? 'info' : 'secondary'} className="text-uppercase" style={{ fontSize: '0.58rem', padding: '2px 4px' }}>
                                {c.contact_type === 'driver' ? 'Conductor' : 'Pasajero'}
                              </Badge>
                            )}
                            {c.pending_quote && (
                              <Badge bg="warning" text="dark" pill className="animate-pulse fw-bold" style={{ fontSize: '0.58rem', padding: '2px 4px' }}>
                                ⚡ COTIZAR
                              </Badge>
                            )}
                            {c.requires_attention && !c.pending_quote && (
                              <Badge bg="danger" pill className="animate-pulse" style={{ fontSize: '0.58rem', padding: '2px 4px' }}>ATENCIÓN</Badge>
                            )}
                            {c.ticket_status === 'RESOLVED' ? (
                              <Badge bg="success" style={{ fontSize: '0.58rem', backgroundColor: '#15803d', padding: '2px 4px' }}>
                                ✓ {c.resolved_by ? `Resuelto (${c.resolved_by})` : 'Resuelto'}
                              </Badge>
                            ) : c.ticket_status === 'IN_PROGRESS' && c.assigned_to ? (
                              <Badge 
                                bg="primary" 
                                style={{ 
                                  fontSize: '0.58rem', 
                                  padding: '2px 4px',
                                  backgroundColor: c.assigned_to.toLowerCase() === operatorName.toLowerCase() ? '#0284c7' : '#475569' 
                                }}
                              >
                                👤 {c.assigned_to}
                              </Badge>
                            ) : (
                              <Badge bg="dark" className="border border-secondary text-secondary" style={{ fontSize: '0.55rem', padding: '1px 3px' }}>
                                ⏳ Cola
                              </Badge>
                            )}
                          </div>

                          {isBookingChat ? (
                            <span className="d-flex align-items-center gap-1 small fw-semibold" style={{ fontSize: '0.63rem', color: '#60a5fa' }}>
                              🏨 App Booking
                            </span>
                          ) : c.is_session_active ? (
                            <span className="d-flex align-items-center gap-1 small text-success" style={{ fontSize: '0.65rem' }}>
                              <Unlock size={9} /> 24h
                            </span>
                          ) : (
                            <span className="d-flex align-items-center gap-1 small text-warning" style={{ fontSize: '0.65rem' }}>
                              <Lock size={9} /> Plantilla
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Botón ACEPTAR estilo Whaticket a la derecha (si está en espera) */}
                    {isPendingQueue && (
                      <div 
                        className="d-flex align-items-center justify-content-center p-1"
                        style={{ borderLeft: '1px solid #1e293b' }}
                      >
                        <button
                          type="button"
                          className="btn d-flex flex-column align-items-center justify-content-center rounded-2 border-0 shadow-sm"
                          style={{
                            backgroundColor: '#93c5fd',
                            color: '#0f172a',
                            width: '56px',
                            minWidth: '56px',
                            height: '80%',
                            padding: '4px',
                            transition: 'all 0.15s ease'
                          }}
                          title="Tomar esta conversación (asigna a ti y silencia la IA)"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAcceptChat(c);
                          }}
                        >
                          <CheckCircle2 size={16} className="text-dark mb-1" />
                          <span style={{ fontSize: '0.68rem', fontWeight: '700', lineHeight: 1 }}>Aceptar</span>
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Botones inferiores: Buscar/Leer historial sin enviar plantilla + Iniciar conversación con plantilla */}
        <div className="p-2 border-top d-flex flex-column gap-1" style={{ backgroundColor: '#0f172a', borderColor: '#1e293b' }}>
          <Button
            variant="outline-info"
            size="sm"
            className="w-100 py-1.5 fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm"
            style={{ backgroundColor: '#1e293b', borderColor: '#0284c7', color: '#7dd3fc', fontSize: '0.78rem', borderRadius: '8px' }}
            onClick={handleOpenHistorySearchModal}
            title="Buscar y abrir una conversación anterior solo para leer (NO envía plantilla ni mensajes)"
          >
            <Eye size={15} />
            <span>Buscar Chat / Leer Historial</span>
          </Button>
          <Button
            variant="primary"
            size="sm"
            className="w-100 py-1.5 fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm"
            style={{ backgroundColor: '#7c3aed', borderColor: '#6d28d9', fontSize: '0.78rem', borderRadius: '8px' }}
            onClick={handleOpenNewConversationModal}
            title="Iniciar conversación enviando plantilla oficial de WhatsApp"
          >
            <span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white text-dark fw-bold" style={{ width: '18px', height: '18px' }}>
              <Plus size={13} strokeWidth={3} />
            </span>
            <span>Iniciar Conversación / Plantilla</span>
          </Button>
        </div>
      </div>

      {/* 2. CHAT PANEL (FULL WIDTH) */}
      <div className="flex-grow-1" style={{ display: 'flex', flexDirection: 'column', backgroundColor: '#0b1120' }}>
        {selectedChat ? (
          <>
            {/* Active Chat Header */}
            <div
              className="p-3 border-bottom d-flex justify-content-between align-items-center text-white"
              style={{
                backgroundColor: isBookingChatItem(selectedChat) ? '#071c3d' : (isTransfeeroChatItem(selectedChat) ? '#052e23' : '#0f172a'),
                borderColor: isBookingChatItem(selectedChat) ? '#1d4ed8' : (isTransfeeroChatItem(selectedChat) ? '#10b981' : '#1e293b')
              }}
            >
              <div>
                <h6 className="mb-0 fw-bold text-white d-flex align-items-center gap-2 flex-wrap">
                  {isBookingChatItem(selectedChat) ? (
                    <span
                      className="badge fw-bold d-inline-flex align-items-center gap-1 shadow-sm"
                      style={{
                        backgroundColor: '#003580',
                        color: '#ffffff',
                        border: '1px solid #60a5fa',
                        fontSize: '0.72rem',
                        padding: '4px 8px'
                      }}
                    >
                      🏨 BOOKING.COM LIVE CHAT
                    </span>
                  ) : isTransfeeroChatItem(selectedChat) ? (
                    <span
                      className="badge fw-bold d-inline-flex align-items-center gap-1 shadow-sm"
                      style={{
                        backgroundColor: '#065f46',
                        color: '#ffffff',
                        border: '1px solid #34d399',
                        fontSize: '0.72rem',
                        padding: '4px 8px'
                      }}
                    >
                      🚘 TRANSFEERO OPERACIONES
                    </span>
                  ) : (
                    <span
                      className="badge fw-bold d-inline-flex align-items-center gap-1"
                      style={{
                        backgroundColor: '#15803d',
                        color: '#ffffff',
                        fontSize: '0.7rem',
                        padding: '4px 8px'
                      }}
                    >
                      📱 WHATSAPP
                    </span>
                  )}
                  <span>👤 {selectedChat.contact_name || selectedChat.phone_number}</span>
                  {!isBookingChatItem(selectedChat) && !isTransfeeroChatItem(selectedChat) && (
                    <Badge bg={selectedChat.contact_type === 'driver' ? 'info' : 'secondary'} className="ms-1" style={{ fontSize: '0.65rem' }}>
                      {selectedChat.contact_type === 'driver' ? 'Chofer' : 'Pasajero'}
                    </Badge>
                  )}
                  {selectedChat.last_trip_id && (
                    <Badge bg="dark" className="border border-secondary" style={{ fontSize: '0.65rem' }}>
                      🚗 Viaje EmiGo: #{selectedChat.last_trip_id}
                    </Badge>
                  )}
                  {selectedChat.ticket_status === 'RESOLVED' ? (
                    <Badge bg="success" className="ms-1 d-inline-flex align-items-center gap-1" style={{ fontSize: '0.72rem', backgroundColor: '#16a34a' }}>
                      <CheckCircle2 size={12} />
                      <span>Resuelto por:</span>
                      <strong>{selectedChat.resolved_by || selectedChat.last_read_by || 'Operador'}</strong>
                      {selectedChat.resolved_at && (
                        <span className="opacity-75">({new Date(selectedChat.resolved_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})</span>
                      )}
                    </Badge>
                  ) : selectedChat.ticket_status === 'IN_PROGRESS' && selectedChat.assigned_to ? (
                    <Badge 
                      bg="primary" 
                      className="ms-1 d-inline-flex align-items-center gap-1 shadow-sm" 
                      style={{ 
                        fontSize: '0.72rem', 
                        backgroundColor: selectedChat.assigned_to.toLowerCase() === operatorName.toLowerCase() ? '#0284c7' : '#475569' 
                      }}
                    >
                      <UserCheck size={12} />
                      <span>{selectedChat.assigned_to.toLowerCase() === operatorName.toLowerCase() ? 'Tomado por ti' : `Asignado a: ${selectedChat.assigned_to}`}</span>
                      {selectedChat.assigned_at && (
                        <span className="opacity-75">({new Date(selectedChat.assigned_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})</span>
                      )}
                    </Badge>
                  ) : (
                    <Badge bg="dark" className="border border-warning text-warning ms-1 d-inline-flex align-items-center gap-1" style={{ fontSize: '0.7rem' }}>
                      <Clock size={11} />
                      <span>En espera / Cola</span>
                    </Badge>
                  )}
                  {!isBookingChatItem(selectedChat) && !isTransfeeroChatItem(selectedChat) && selectedChat.conversation_state && selectedChat.conversation_state !== 'IDLE' && (
                    <Badge 
                      bg={
                        selectedChat.conversation_state === 'AWAITING_PAYMENT' ? 'warning' :
                        selectedChat.conversation_state === 'TRANSFERRED_LOCUTORA' ? 'danger' :
                        selectedChat.conversation_state === 'BOOKING_AWAITING_CONFIRMATION' ? 'info' : 'primary'
                      }
                      text={selectedChat.conversation_state === 'AWAITING_PAYMENT' ? 'dark' : 'white'}
                      style={{ fontSize: '0.65rem' }}
                    >
                      {selectedChat.conversation_state === 'AWAITING_PAYMENT' && '💳 Esperando Pago dLocal'}
                      {selectedChat.conversation_state === 'TRANSFERRED_LOCUTORA' && '🚨 Despacho Requerido'}
                      {selectedChat.conversation_state === 'BOOKING_AWAITING_CONFIRMATION' && '⏳ Esperando Confirmación'}
                      {['BOOKING_COLLECT_INFO', 'BOOKING_ASK_CONVENIO'].includes(selectedChat.conversation_state) && '⚡ IA Reservando'}
                    </Badge>
                  )}
                </h6>
                {isBookingChatItem(selectedChat) ? (
                  <small style={{ color: '#93c5fd' }}>
                    🏨 Canal Oficial <strong>Booking.com Live Chat</strong> • Reserva Booking: <strong>#{selectedChat.booking_reference || String(selectedChat.phone_number || '').replace('booking:', '')}</strong> (El conductor también recibe y puede responder desde su Portal)
                  </small>
                ) : isTransfeeroChatItem(selectedChat) ? (
                  <small style={{ color: '#6ee7b7' }}>
                    🚘 Canal Operativo <strong>Transfeero / Movio</strong> • Referencia: <strong>#{selectedChat.booking_reference || String(selectedChat.phone_number || '').replace('transfeero:', '')}</strong> (Puedes dejar notas internas aquí o revisar en control.transfeero.com)
                  </small>
                ) : (
                  <small style={{ color: '#94a3b8' }}>📱 Canal <strong>WhatsApp</strong> • Teléfono: {selectedChat.phone_number?.startsWith('+') ? selectedChat.phone_number : `+${selectedChat.phone_number}`}</small>
                )}
              </div>

              <div className="d-flex gap-2 align-items-center flex-wrap justify-content-end">
                {/* Botón Aceptar Chat si está en cola o resuelto */}
                {(!selectedChat.ticket_status || selectedChat.ticket_status === 'PENDING' || selectedChat.ticket_status === 'RESOLVED') && (
                  <Button 
                    size="sm" 
                    variant="success" 
                    className="fw-bold d-flex align-items-center gap-1 shadow-sm"
                    style={{ backgroundColor: '#16a34a', borderColor: '#16a34a', fontSize: '0.75rem' }}
                    onClick={() => handleAcceptChat(selectedChat)}
                    disabled={processingTicketAction}
                    title="Aceptar y tomar esta conversación"
                  >
                    <UserCheck size={14} />
                    <span>Aceptar Chat</span>
                  </Button>
                )}

                {/* Si ya está tomado: Botón Resolver y Opciones */}
                {selectedChat.ticket_status === 'IN_PROGRESS' && (
                  <div className="d-flex gap-1 align-items-center">
                    <Button 
                      size="sm" 
                      variant="success" 
                      className="fw-bold d-flex align-items-center gap-1 shadow-sm"
                      style={{ backgroundColor: '#059669', borderColor: '#059669', fontSize: '0.75rem' }}
                      onClick={() => handleResolveChat(selectedChat)}
                      disabled={processingTicketAction}
                      title="Marcar como resuelto y cerrar atención"
                    >
                      <CheckCircle2 size={14} />
                      <span>Resolver</span>
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline-secondary" 
                      className="d-flex align-items-center gap-1"
                      style={{ borderColor: '#475569', color: '#cbd5e1', fontSize: '0.72rem' }}
                      onClick={() => handleReassignChat(null)}
                      disabled={processingTicketAction}
                      title="Devolver a la cola general para otra compañera"
                    >
                      <ArrowRightLeft size={13} />
                      <span>A Cola</span>
                    </Button>
                  </div>
                )}

                {!isBookingChatItem(selectedChat) && ((selectedChat.conversation_state && selectedChat.conversation_state !== 'IDLE') || selectedChat.requires_attention) && (
                  <Button
                    size="sm"
                    variant="outline-warning"
                    style={{ fontSize: '0.72rem' }}
                    onClick={async () => {
                      try {
                        await resetWhatsAppChatAI(selectedChat.phone_number);
                        toast.success("IA reiniciada a estado normal.");
                        setSelectedChat(prev => prev ? { ...prev, conversation_state: 'IDLE', requires_attention: false, pending_quote: false } : null);
                        setChats(prev => prev.map(c => c.phone_number === selectedChat.phone_number ? { ...c, conversation_state: 'IDLE', requires_attention: false, pending_quote: false } : c));
                        fetchChats(false);
                      } catch (err) {
                        toast.error("Error reiniciando IA.");
                      }
                    }}
                    title="Reiniciar flujo de IA en este chat"
                  >
                    🔄 Reset IA
                  </Button>
                )}
                <Button 
                  size="sm" 
                  variant="outline-info" 
                  style={{ borderColor: '#38bdf8', color: '#38bdf8', fontSize: '0.72rem' }}
                  onClick={() => handleMarkChatAsUnread(selectedChat)}
                  title="Marcar esta conversación como no leída para atenderla más tarde"
                  className="d-flex align-items-center gap-1"
                >
                  <Mail size={13} />
                  <span>No leído</span>
                </Button>
                <Button 
                  size="sm" 
                  variant="outline-secondary" 
                  style={{ borderColor: '#334155', color: '#94a3b8' }}
                  onClick={() => fetchMessages(selectedChat.phone_number)}
                  title="Refrescar mensajes"
                >
                  <RefreshCw size={14} />
                </Button>
              </div>
            </div>

            {/* Banner informativo cuando la conversación es de BOOKING.COM LIVE CHAT */}
            {isBookingChatItem(selectedChat) && (
              <div
                className="px-3 py-2 d-flex justify-content-between align-items-center flex-wrap gap-2"
                style={{
                  backgroundColor: '#00224f',
                  borderBottom: '1px solid #2563eb',
                  color: '#dbeafe',
                  fontSize: '0.78rem'
                }}
              >
                <div className="d-flex align-items-center gap-2">
                  <span>🏨</span>
                  <span>
                    <strong>Chat en Vivo de Booking.com (App del Pasajero):</strong> Los mensajes que envíes aquí llegarán directo a la App de Booking.com del pasajero. <strong>Eva (IA) está desactivada</strong> en este canal; interactúan el <strong>Conductor</strong> y/o <strong>Locutoras</strong>.
                  </span>
                </div>
              </div>
            )}

            {/* Banner Alerta de Locutora Requerida (solo WhatsApp) */}
            {!isBookingChatItem(selectedChat) && (selectedChat.requires_attention || selectedChat.conversation_state === 'TRANSFERRED_LOCUTORA') && (
              <div className="p-3 d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ backgroundColor: '#450a0a', borderBottom: '1px solid #dc2626', color: '#fecaca' }}>
                <div className="d-flex align-items-center gap-2">
                  <span className="fs-5">🚨</span>
                  <div>
                    <div className="fw-bold" style={{ fontSize: '0.88rem', color: '#fca5a5' }}>ATENCIÓN DE LOCUTORA REQUERIDA</div>
                    <div className="small opacity-75" style={{ fontSize: '0.75rem', color: '#fecaca' }}>
                      El pasajero solicitó asistencia de una locutora o la IA transfirió la conversación para despacho manual.
                    </div>
                  </div>
                </div>
                <div className="d-flex gap-2">
                  <Button 
                    size="sm" 
                    variant="danger"
                    className="fw-bold"
                    style={{ fontSize: '0.75rem', backgroundColor: '#dc2626', borderColor: '#dc2626' }}
                    onClick={async () => {
                      try {
                        await resetWhatsAppChatAI(selectedChat.phone_number);
                        toast.success("Atención resuelta y chat reactivado con IA.");
                        setSelectedChat(prev => prev ? { ...prev, conversation_state: 'IDLE', requires_attention: false } : null);
                        setChats(prev => prev.map(c => c.phone_number === selectedChat.phone_number ? { ...c, conversation_state: 'IDLE', requires_attention: false } : c));
                        window.dispatchEvent(new CustomEvent('whatsapp_unread_updated'));
                      } catch (e) {
                        toast.error("Error al resolver atención.");
                      }
                    }}
                  >
                    ✓ Marcar Atendido / Reactivar IA
                  </Button>
                </div>
              </div>
            )}

            {/* Banner Alerta de Cotización Pendiente */}
            {selectedChat.pending_quote && (
              <div className="p-3 d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ backgroundColor: '#451a03', borderBottom: '1px solid #b45309', color: '#fef3c7' }}>
                <div className="d-flex align-items-center gap-2">
                  <span className="fs-5">⚡</span>
                  <div>
                    <div className="fw-bold" style={{ fontSize: '0.88rem', color: '#fef08a' }}>SOLICITUD DE COTIZACIÓN PENDIENTE</div>
                    <div className="small opacity-75" style={{ fontSize: '0.75rem', color: '#fde68a' }}>El pasajero solicitó una cotización. Envía la tarifa oficial o márcala como atendida cuando esté lista.</div>
                  </div>
                </div>
                <div className="d-flex gap-2">
                  <Button 
                    size="sm" 
                    variant="warning"
                    className="fw-bold text-dark"
                    style={{ fontSize: '0.75rem', backgroundColor: '#f59e0b', borderColor: '#f59e0b' }}
                    onClick={() => {
                      setMessageText(prev => prev ? prev : "Hola, con gusto. Para tu servicio, la tarifa es de $");
                    }}
                  >
                    💬 Responder Tarifa
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline-light"
                    style={{ fontSize: '0.75rem' }}
                    onClick={handleResolveQuote}
                  >
                    ✓ Marcar Atendida
                  </Button>
                </div>
              </div>
            )}

            <div className="flex-grow-1 overflow-hidden d-flex flex-column" style={{ minHeight: 0 }}>
              
              {/* Message List Grid */}
              <div className="p-3 overflow-auto flex-grow-1 d-flex flex-column gap-3" style={{ backgroundColor: '#090d16' }}>
                {loadingMessages ? (
                  <div className="text-center p-5">
                    <Spinner animation="border" style={{ color: '#a855f7' }} />
                    <p className="small mt-2" style={{ color: '#94a3b8' }}>Cargando conversación...</p>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center p-5 small my-auto" style={{ color: '#64748b' }}>
                    Historial vacío. Escribe un mensaje para comenzar la conversación.
                  </div>
                ) : (
                  <>
                    {/* Chip de auditoría: quién abrió la conversación */}
                    {selectedChat.last_read_by && (
                      <div className="d-flex justify-content-center my-1">
                        <div 
                          className="px-3 py-1 rounded-pill small d-flex align-items-center gap-1 shadow-sm"
                          style={{ 
                            backgroundColor: 'rgba(56, 189, 248, 0.08)', 
                            border: '1px solid rgba(56, 189, 248, 0.25)', 
                            color: '#7dd3fc', 
                            fontSize: '0.72rem' 
                          }}
                        >
                          <span>👀 Conversación abierta y atendida por <strong>{selectedChat.last_read_by}</strong></span>
                          {selectedChat.last_read_at && (
                            <span className="text-white-50">({new Date(selectedChat.last_read_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})</span>
                          )}
                        </div>
                      </div>
                    )}

                    {hasMoreMessages && (
                      <div className="text-center py-2 mb-2">
                        <Button 
                          variant="outline-secondary" 
                          size="sm" 
                          onClick={loadOlderMessages}
                          disabled={loadingOlder}
                          style={{ borderColor: '#334155', color: '#94a3b8', fontSize: '0.75rem' }}
                        >
                          {loadingOlder ? <Spinner size="sm" animation="border" /> : "Cargar mensajes anteriores"}
                        </Button>
                      </div>
                    )}

                    {messages.map((m, index) => {
                      const isOutbound = m.direction === 'outbound';
                      const timeStr = new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                      const currentDateLabel = getMessageDateLabel(m.created_at);
                      const prevDateLabel = index > 0 ? getMessageDateLabel(messages[index - 1].created_at) : null;
                      const showDateSeparator = currentDateLabel !== prevDateLabel;

                      // Detectar firma de operadora o emisor si existe
                      const signatureMatch = isOutbound && m.content && m.content.match(/^\*([^*]+):\*\n([\s\S]*)$/);
                      const effectiveSender = m.sender_name || (signatureMatch ? signatureMatch[1] : null);
                      const rawDisplayContent = signatureMatch ? signatureMatch[2] : (m.content || '');
                      const mediaIdMatch = rawDisplayContent.match(/\[MEDIA_ID:([^\]]+)\]/);
                      const mediaId = mediaIdMatch ? mediaIdMatch[1] : null;
                      const mapUrlMatch = rawDisplayContent.match(/\[MAP_URL:([^\r\n]+)\]/);
                      const mapUrl = mapUrlMatch ? mapUrlMatch[1].replace(/\[/g, '%5B').replace(/\]/g, '%5D') : null;
                      const mediaProxyUrl = mediaId ? `${API_BASE_URL}/transport/whatsapp/media/${mediaId}` : mapUrl;
                      const displayContent = rawDisplayContent
                        .replace(/\[MEDIA_ID:[^\]]+\]\s*/g, '')
                        .replace(/\[MAP_URL:[^\r\n]+\]\s*/g, '')
                        .replace(/\[Imagen Mapa de Ruta\]\s*/g, '')
                        .replace(/\[AI_OCR\][\s\S]*?\[\/AI_OCR\]\s*/g, '')
                        .trim();

                      return (
                        <React.Fragment key={m.id || index}>
                          {showDateSeparator && (
                            <div className="d-flex justify-content-center my-2">
                              <span 
                                className="px-3 py-1 rounded-pill small fw-semibold" 
                                style={{ backgroundColor: '#1e293b', color: '#94a3b8', fontSize: '0.72rem', border: '1px solid #334155' }}
                              >
                                {currentDateLabel}
                              </span>
                            </div>
                          )}

                          {/* Mensajes de eventos del sistema estilo Whaticket (Aceptación, Resolución, Transferencia) */}
                          {m.direction === 'system' || m.message_type === 'system_event' ? (
                            <div className="d-flex justify-content-center my-2">
                              <div 
                                className="px-3 py-1 rounded-pill small fw-semibold text-center shadow-sm d-inline-flex align-items-center gap-1"
                                style={{
                                  backgroundColor: '#1e293b',
                                  border: '1px solid #475569',
                                  color: '#cbd5e1',
                                  fontSize: '0.73rem',
                                  maxWidth: '85%'
                                }}
                              >
                                {m.content?.toLowerCase().includes('resolvió') ? (
                                  <CheckCircle2 size={13} className="text-success" />
                                ) : m.content?.toLowerCase().includes('aceptó') ? (
                                  <UserCheck size={13} className="text-info" />
                                ) : (
                                  <ArrowRightLeft size={13} className="text-warning" />
                                )}
                                <span>{m.content}</span>
                                <span className="opacity-75 ms-1" style={{ fontSize: '0.67rem' }}>{timeStr}</span>
                              </div>
                            </div>
                          ) : (
                            <div className={`d-flex ${isOutbound ? 'justify-content-end' : 'justify-content-start'}`}>
                            <div 
                              className={`p-3 rounded-3 position-relative ${isOutbound ? 'rounded-bottom-end-0' : 'rounded-bottom-start-0'}`} 
                              style={{ 
                                maxWidth: '75%',
                                backgroundColor: isOutbound
                                  ? (m.sender_role === 'DRIVER' ? '#0f3d3e' : (isBookingChatItem(selectedChat) ? '#003580' : '#3b0764'))
                                  : '#1e293b', 
                                border: isOutbound
                                  ? (m.sender_role === 'DRIVER' ? '1px solid #14b8a6' : (isBookingChatItem(selectedChat) ? '1px solid #3b82f6' : '1px solid #6b21a8'))
                                  : '1px solid #334155',
                                color: '#ffffff'
                              }}
                            >
                              {/* Nombre de la locutora, conductor o remitente estilo Whaticket */}
                              {isOutbound ? (
                                <div className="d-flex align-items-center justify-content-between gap-2 mb-1 pb-1" style={{ fontSize: '0.73rem', borderBottom: '1px solid rgba(255, 255, 255, 0.15)' }}>
                                  <span className="fw-bold" style={{ color: m.sender_role === 'DRIVER' ? '#5eead4' : (isBookingChatItem(selectedChat) ? '#93c5fd' : '#d8b4fe') }}>
                                    {m.sender_role === 'DRIVER' ? (
                                      `🚗 ${effectiveSender || 'Conductor'}`
                                    ) : effectiveSender ? (
                                      effectiveSender.toLowerCase().includes('asistente') || effectiveSender.toLowerCase().includes('bot') || effectiveSender.toLowerCase().includes('eva')
                                        ? `🤖 ${effectiveSender}`
                                        : `🎧 ${effectiveSender}`
                                    ) : '🎧 Locutora'}
                                  </span>
                                  {isBookingChatItem(selectedChat) && (
                                    <span className="badge" style={{ backgroundColor: 'rgba(0,0,0,0.25)', fontSize: '0.6rem', color: '#bfdbfe' }}>
                                      🏨 Booking Chat
                                    </span>
                                  )}
                                </div>
                              ) : isBookingChatItem(selectedChat) ? (
                                <div className="d-flex align-items-center justify-content-between gap-2 mb-1 pb-1" style={{ fontSize: '0.72rem', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                  <span className="fw-bold" style={{ color: '#60a5fa' }}>
                                    👤 {m.sender_name || selectedChat.contact_name || 'Pasajero Booking.com'}
                                  </span>
                                  <span className="badge" style={{ backgroundColor: '#003580', fontSize: '0.6rem', color: '#fff' }}>
                                    🏨 App Booking
                                  </span>
                                </div>
                              ) : null}

                              {mediaProxyUrl && (
                                <div className="mb-1">
                                  <img
                                    src={mediaProxyUrl}
                                    alt="Imagen enviada por WhatsApp"
                                    loading="lazy"
                                    onClick={() => setPreviewImageUrl(mediaProxyUrl)}
                                    title="Haz clic para ver la imagen en tamaño completo"
                                    style={{
                                      maxWidth: '280px',
                                      maxHeight: '320px',
                                      width: '100%',
                                      objectFit: 'cover',
                                      borderRadius: '8px',
                                      cursor: 'pointer',
                                      border: '1px solid rgba(255, 255, 255, 0.15)',
                                      display: 'block'
                                    }}
                                  />
                                </div>
                              )}

                              {displayContent && (
                                (m.message_type === 'contact' || displayContent.includes('👤 *Contacto compartido:*')) ? (
                                  <div
                                    className="p-2 rounded-2 my-1"
                                    style={{
                                      backgroundColor: 'rgba(15, 23, 42, 0.55)',
                                      border: '1px solid rgba(148, 163, 184, 0.3)',
                                      borderLeft: '3px solid #38bdf8',
                                      whiteSpace: 'pre-wrap',
                                      fontSize: '0.88rem'
                                    }}
                                  >
                                    {displayContent.replace(/\*/g, '')}
                                  </div>
                                ) : (
                                  <div className="message-content text-break" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                                    {displayContent}
                                  </div>
                                )
                              )}

                              {/* Traducción inline del mensaje del pasajero al Español */}
                              {!isOutbound && messageTranslations[m.id || index] && (
                                <div
                                  className="mt-2 p-2 rounded-2"
                                  style={{
                                    backgroundColor: 'rgba(14, 165, 233, 0.12)',
                                    border: '1px solid rgba(56, 189, 248, 0.35)',
                                    borderLeft: '3px solid #38bdf8',
                                    whiteSpace: 'pre-wrap',
                                    fontSize: '0.85rem',
                                    color: '#e0f2fe'
                                  }}
                                >
                                  <div className="d-flex justify-content-between align-items-center mb-1" style={{ fontSize: '0.7rem', color: '#7dd3fc' }}>
                                    <span className="fw-bold">
                                      🌐 Traducción al Español {messageTranslations[m.id || index].sourceLang ? `(${messageTranslations[m.id || index].sourceLang})` : ''}:
                                    </span>
                                  </div>
                                  <div>{messageTranslations[m.id || index].text}</div>
                                </div>
                              )}

                              <div className="d-flex justify-content-between align-items-center gap-2 mt-1" style={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                                {!isOutbound && displayContent ? (
                                  <button
                                    type="button"
                                    onClick={() => handleTranslateIncomingMessage(m.id || index, displayContent)}
                                    disabled={translatingMsgId === (m.id || index)}
                                    className="btn btn-link p-0 text-decoration-none d-inline-flex align-items-center gap-1"
                                    style={{ fontSize: '0.7rem', color: messageTranslations[m.id || index] ? '#38bdf8' : '#94a3b8' }}
                                    title="Traducir mensaje del pasajero al español"
                                  >
                                    {translatingMsgId === (m.id || index) ? (
                                      <>
                                        <Spinner size="sm" animation="border" style={{ width: '10px', height: '10px' }} />
                                        <span>Traduciendo...</span>
                                      </>
                                    ) : (
                                      <>
                                        <Globe size={11} />
                                        <span>{messageTranslations[m.id || index] ? 'Ocultar traducción' : 'Traducir'}</span>
                                      </>
                                    )}
                                  </button>
                                ) : <span />}
                                <div className="d-flex align-items-center gap-1">
                                  <span>{timeStr}</span>
                                  {isOutbound && getStatusIcon(m.status)}
                                </div>
                              </div>
                              
                              {m.trip_id && (m.message_type === 'template' || m.message_type === 'notification') && (
                                <Badge bg="dark" className="mt-1 d-block text-start text-xs border border-secondary" style={{ fontSize: '0.65rem' }}>
                                  🚗 Notificación Reserva #{m.trip_id}
                                </Badge>
                              )}

                              {m.error_message && (
                                <div className="text-danger small mt-1 border-top border-danger pt-1" style={{ fontSize: '0.7rem' }}>
                                  ⚠️ {m.error_message}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        </React.Fragment>
                      );
                    })}
                  </>
                )}
                <div ref={messageEndRef} />
              </div>

              {/* 3. INPUT ZONE */}
              <div className="p-3 border-top" style={{ backgroundColor: '#0f172a', borderColor: '#1e293b' }}>
                {selectedImagePreview && (
                  <div 
                    className="d-flex align-items-center justify-content-between p-2 mb-2 rounded-2"
                    style={{ backgroundColor: '#1e293b', border: '1px solid #475569' }}
                  >
                    <div className="d-flex align-items-center gap-2 overflow-hidden">
                      <img
                        src={selectedImagePreview}
                        alt="Vista previa adjunto"
                        style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #64748b' }}
                      />
                      <div className="text-truncate">
                        <div className="text-white small fw-semibold text-truncate" style={{ maxWidth: '260px' }}>
                          📷 {selectedImageFile?.name || 'Imagen lista para enviar'}
                        </div>
                        <div className="text-secondary" style={{ fontSize: '0.72rem' }}>
                          {selectedImageFile ? `${(selectedImageFile.size / 1024).toFixed(0)} KB — Puedes agregar un comentario abajo` : ''}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="p-1 d-flex align-items-center justify-content-center"
                      onClick={handleClearImage}
                      disabled={sendingMessage}
                      title="Quitar imagen adjunta"
                    >
                      <X size={15} />
                    </Button>
                  </div>
                )}

                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) handleSelectImageFile(file);
                  }}
                />

                <Form onSubmit={handleSendFreeText} className="d-flex gap-2 align-items-center">
                  {/* Botón '+' / Plantilla y Adjuntar Imagen sólo en canal WhatsApp */}
                  {!isBookingChatItem(selectedChat) && !isTransfeeroChatItem(selectedChat) && (
                    <>
                      <Button
                        variant="outline-primary"
                        type="button"
                        className="px-2.5 py-2 d-flex align-items-center justify-content-center gap-1 fw-semibold flex-shrink-0"
                        style={{ borderColor: '#7c3aed', color: '#ddd6fe', backgroundColor: 'rgba(124, 58, 237, 0.18)', fontSize: '0.78rem' }}
                        onClick={() => handleOpenChatTemplateModal(selectedChat)}
                        disabled={sendingMessage}
                        title="Enviar plantilla de inicio de conversación (2 variables editables) o buscar otro pasajero"
                      >
                        <Plus size={16} strokeWidth={2.5} />
                        <span className="d-none d-md-inline">Plantilla</span>
                      </Button>
                      <Button
                        variant="outline-secondary"
                        type="button"
                        className="px-2.5 py-2 d-flex align-items-center justify-content-center"
                        style={{ borderColor: '#334155', color: selectedImageFile ? '#a855f7' : '#cbd5e1', backgroundColor: '#1e293b' }}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={sendingMessage}
                        title="Adjuntar imagen (o pega un pantallazo con Ctrl+V / Cmd+V)"
                      >
                        <Image size={18} />
                      </Button>
                    </>
                  )}
                  <Form.Control
                    type="text"
                    placeholder={
                      isBookingChatItem(selectedChat)
                        ? "Escribe al pasajero por Booking.com Live Chat (máx. 350 caracteres, puedes usar 🌐 Traducir)..."
                        : isTransfeeroChatItem(selectedChat)
                          ? "Deja una nota interna de despacho para esta alerta Transfeero..."
                          : selectedImageFile
                            ? "Escribe un pie de foto opcional..."
                            : "Escribe en español (puedes usar 🌐 Traducir antes de enviar)..."
                    }
                    maxLength={isBookingChatItem(selectedChat) ? 350 : undefined}
                    style={{
                      backgroundColor: '#1e293b',
                      borderColor: isBookingChatItem(selectedChat) ? '#2563eb' : (isTransfeeroChatItem(selectedChat) ? '#10b981' : '#334155'),
                      color: '#ffffff'
                    }}
                    className="py-2"
                    value={messageText}
                    onChange={e => setMessageText(e.target.value)}
                    onPaste={(isBookingChatItem(selectedChat) || isTransfeeroChatItem(selectedChat)) ? undefined : handlePasteImage}
                    disabled={sendingMessage || translatingDraft}
                    autoFocus
                  />
                  {/* Selector de idioma destino + botón Traducir borrador de la locutora */}
                  {!isTransfeeroChatItem(selectedChat) && (
                    <div className="d-flex align-items-center flex-shrink-0" style={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '6px' }}>
                      <Form.Select
                        size="sm"
                        value={outboundTargetLang}
                        onChange={e => setOutboundTargetLang(e.target.value)}
                        disabled={sendingMessage || translatingDraft}
                        title="Idioma al que se traducirá tu respuesta"
                        style={{
                          backgroundColor: 'transparent',
                          border: 'none',
                          color: '#93c5fd',
                          fontSize: '0.74rem',
                          fontWeight: 600,
                          paddingRight: '1.4rem',
                          width: 'auto',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="auto_passenger" style={{ backgroundColor: '#0f172a', color: '#fff' }}>🌐 Auto</option>
                        <option value="en" style={{ backgroundColor: '#0f172a', color: '#fff' }}>🇬🇧 EN</option>
                        <option value="pt" style={{ backgroundColor: '#0f172a', color: '#fff' }}>🇧🇷 PT</option>
                        <option value="fr" style={{ backgroundColor: '#0f172a', color: '#fff' }}>🇫🇷 FR</option>
                      </Form.Select>
                      <Button
                        variant="link"
                        type="button"
                        className="px-2 py-1.5 text-decoration-none d-flex align-items-center gap-1 fw-semibold border-start"
                        style={{ borderColor: '#334155', color: '#38bdf8', fontSize: '0.76rem' }}
                        onClick={handleTranslateOutboundDraft}
                        disabled={sendingMessage || translatingDraft || !messageText.trim()}
                        title="Traducir lo que escribiste al idioma del pasajero antes de enviarlo"
                      >
                        {translatingDraft ? (
                          <Spinner size="sm" animation="border" style={{ width: '13px', height: '13px' }} />
                        ) : (
                          <>
                            <Globe size={14} />
                            <span className="d-none d-lg-inline">Traducir</span>
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                  <Button 
                    variant="primary" 
                    type="submit" 
                    className="px-3 py-2 d-flex align-items-center justify-content-center"
                    style={{
                      backgroundColor: isBookingChatItem(selectedChat) ? '#003580' : (isTransfeeroChatItem(selectedChat) ? '#059669' : '#8b5cf6'),
                      borderColor: isBookingChatItem(selectedChat) ? '#2563eb' : (isTransfeeroChatItem(selectedChat) ? '#10b981' : '#8b5cf6')
                    }} 
                    disabled={sendingMessage || translatingDraft || (!messageText.trim() && !selectedImageFile)}
                  >
                    {sendingMessage ? <Spinner size="sm" animation="border" /> : <Send size={18} />}
                  </Button>
                </Form>
                {!isBookingChatItem(selectedChat) && !isTransfeeroChatItem(selectedChat) && !selectedChat.is_session_active && (
                  <div className="mt-1.5 d-flex align-items-center justify-content-between flex-wrap gap-2 text-warning" style={{ fontSize: '0.72rem' }}>
                    <div className="d-flex align-items-center gap-1">
                      <Lock size={12} />
                      <span>Ventana 24h inactiva. Si el cliente aún no ha escrito hoy, inicia conversación usando el botón <strong>+ Plantilla</strong>.</span>
                    </div>
                    <Button
                      size="sm"
                      variant="link"
                      className="p-0 text-info fw-bold text-decoration-none"
                      style={{ fontSize: '0.72rem' }}
                      onClick={() => handleOpenChatTemplateModal(selectedChat)}
                    >
                      + Enviar Plantilla ahora
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="d-flex flex-column align-items-center justify-content-center h-100 p-4 text-center" style={{ color: '#64748b' }}>
            <MessageSquare size={60} className="mb-3 p-3 rounded-circle" style={{ width: '90px', height: '90px', backgroundColor: '#1e293b', color: '#8b5cf6', border: '1px solid #334155' }} />
            <h5 className="text-white fw-bold">Centro de Mensajería WhatsApp</h5>
            <p className="small mb-3" style={{ maxWidth: '410px', color: '#94a3b8' }}>
              Selecciona una conversación en el panel izquierdo, busca un chat antiguo para leer su historial sin enviar mensajes, o inicia una conversación con plantilla.
            </p>
            <div className="d-flex flex-wrap justify-content-center gap-2">
              <Button
                variant="outline-info"
                size="sm"
                className="px-3 py-2 fw-bold d-flex align-items-center gap-2 shadow-sm"
                style={{ backgroundColor: '#1e293b', borderColor: '#0284c7', color: '#7dd3fc' }}
                onClick={handleOpenHistorySearchModal}
              >
                <Eye size={16} />
                <span>Buscar Chat / Leer Historial</span>
              </Button>
              <Button
                variant="primary"
                size="sm"
                className="px-3 py-2 fw-bold d-flex align-items-center gap-2 shadow-sm"
                style={{ backgroundColor: '#7c3aed', borderColor: '#6d28d9' }}
                onClick={handleOpenNewConversationModal}
              >
                <Plus size={16} strokeWidth={2.5} />
                <span>Iniciar Conversación con Plantilla</span>
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Modal dedicado: Buscar y Abrir Conversación Histórica (SOLO LECTURA, SIN ENVIAR PLANTILLA) */}
      <Modal
        show={showHistorySearchModal}
        onHide={() => setShowHistorySearchModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton style={{ backgroundColor: '#0f172a', borderColor: '#1e293b', color: '#ffffff' }}>
          <Modal.Title className="fs-6 fw-bold text-white d-flex align-items-center gap-2">
            <Eye size={18} style={{ color: '#38bdf8' }} />
            <span>Buscar Conversación Anterior (Solo Lectura)</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3" style={{ backgroundColor: '#0b1120', color: '#e2e8f0' }}>
          <div
            className="p-2.5 mb-3 rounded-2 small d-flex align-items-center gap-2"
            style={{ backgroundColor: 'rgba(14, 165, 233, 0.12)', border: '1px solid rgba(56, 189, 248, 0.3)', color: '#bae6fd' }}
          >
            <Eye size={16} className="flex-shrink-0 text-info" />
            <span>
              Busca cualquier chat histórico (incluso anterior a los últimos 100 o ya resuelto) por <strong>nombre, teléfono, #ID de viaje o código Booking</strong>. Al abrirlo <strong>no se enviará ninguna plantilla ni mensaje</strong>.
            </span>
          </div>

          <Form
            role="search"
            onSubmit={e => {
              e.preventDefault();
            }}
          >
            <Form.Group className="mb-3">
              <Form.Label htmlFor="whatsapp-history-search-input" className="small fw-bold text-white mb-1">
                Buscar en todo el historial de conversaciones
              </Form.Label>
              <InputGroup>
                <InputGroup.Text style={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#94a3b8' }}>
                  <Search size={15} />
                </InputGroup.Text>
                <Form.Control
                  id="whatsapp-history-search-input"
                  name="history_query"
                  type="search"
                  placeholder="Ej: nombre del pasajero, +569..., #76126 o código Booking..."
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  enterKeyHint="search"
                  style={{ backgroundColor: '#1e293b', borderColor: '#0284c7', color: '#ffffff' }}
                  value={historySearchQuery}
                  onChange={e => setHistorySearchQuery(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.nativeEvent?.isComposing) {
                      e.preventDefault();
                      if (historySearchResults.length === 1) {
                        handleOpenReadOnlyChat(historySearchResults[0]);
                      }
                    }
                  }}
                  autoFocus
                />
                {searchingHistoryModal && (
                  <InputGroup.Text style={{ backgroundColor: '#1e293b', borderColor: '#334155' }}>
                    <Spinner size="sm" animation="border" style={{ color: '#38bdf8' }} />
                  </InputGroup.Text>
                )}
                {historySearchQuery && (
                  <Button
                    type="button"
                    variant="outline-secondary"
                    style={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#cbd5e1' }}
                    onClick={() => setHistorySearchQuery('')}
                    title="Limpiar"
                  >
                    <X size={14} />
                  </Button>
                )}
              </InputGroup>
            </Form.Group>
          </Form>

          {/* Lista de conversaciones históricas encontradas */}
          <div
            aria-live="polite"
            className="rounded-3 overflow-auto"
            style={{ maxHeight: '360px', minHeight: '140px', backgroundColor: '#0f172a', border: '1px solid #1e293b' }}
          >
            {historySearchQuery.trim().replace(/^#/, '').length < 2 ? (
              <div className="p-4 text-center small" style={{ color: '#94a3b8' }}>
                Escribe al menos 2 letras o números para buscar en toda la base de datos de chats y viajes.
              </div>
            ) : searchingHistoryModal && historySearchResults.length === 0 ? (
              <div className="p-4 text-center small" style={{ color: '#94a3b8' }}>
                <Spinner size="sm" animation="border" className="me-2" style={{ color: '#38bdf8' }} />
                Buscando conversaciones anteriores...
              </div>
            ) : historySearchResults.length === 0 ? (
              <div className="p-4 text-center small" style={{ color: '#94a3b8' }}>
                No se encontraron conversaciones previas con &quot;{historySearchQuery.trim()}&quot;.
                {historySearchQuery.replace(/\D/g, '').length >= 8 && (
                  <div className="mt-3">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline-info"
                      onClick={() =>
                        handleOpenReadOnlyChat({
                          phone_number: historySearchQuery.replace(/\D/g, ''),
                          contact_name: `+${historySearchQuery.replace(/\D/g, '')}`
                        })
                      }
                    >
                      👁️ Abrir visor para +{historySearchQuery.replace(/\D/g, '')} de todas formas (sin plantilla)
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              historySearchResults.map((item, idx) => {
                const isBooking = isBookingChatItem(item);
                const formattedDate = item.last_message_at
                  ? new Date(item.last_message_at).toLocaleString('es-CL', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  : 'Sin fecha reciente';
                return (
                  <div
                    key={`${item.phone_number}-${idx}`}
                    className="p-2.5 px-3 d-flex justify-content-between align-items-center gap-2 border-bottom"
                    style={{ borderColor: '#1e293b', cursor: 'pointer' }}
                    onClick={() => handleOpenReadOnlyChat(item)}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#172554')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    <div className="overflow-hidden" style={{ minWidth: 0 }}>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span className="fw-bold text-white" style={{ fontSize: '0.88rem' }}>
                          {item.contact_name || item.phone_number}
                        </span>
                        <Badge bg={isBooking ? 'primary' : 'success'} style={{ fontSize: '0.62rem' }}>
                          {isBooking ? `🏨 Booking #${item.booking_reference || String(item.phone_number).replace('booking:', '')}` : `📱 +${String(item.phone_number || '').replace(/^\+/, '')}`}
                        </Badge>
                        {item.last_trip_id && (
                          <Badge bg="secondary" style={{ fontSize: '0.62rem' }}>
                            🚗 Viaje #{item.last_trip_id}
                          </Badge>
                        )}
                        <span className="small" style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                          {formattedDate}
                        </span>
                      </div>
                      {item.last_message_preview && (
                        <div className="small text-truncate mt-1" style={{ color: '#cbd5e1', fontSize: '0.78rem' }}>
                          {item.last_message_direction === 'outbound' ? 'Tú: ' : ''}
                          {item.last_message_preview}
                        </div>
                      )}
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="info"
                      className="fw-bold flex-shrink-0 d-flex align-items-center gap-1"
                      style={{ fontSize: '0.74rem', backgroundColor: '#0284c7', borderColor: '#0369a1', color: '#ffffff' }}
                      onClick={e => {
                        e.stopPropagation();
                        handleOpenReadOnlyChat(item);
                      }}
                    >
                      <Eye size={13} />
                      <span>Abrir chat</span>
                    </Button>
                  </div>
                );
              })
            )}
          </div>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#0f172a', borderColor: '#1e293b' }}>
          <Button variant="secondary" size="sm" onClick={() => setShowHistorySearchModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal para Enviar Plantilla de Inicio de Conversación (2 Variables Editables + Buscador Histórico) */}
      <Modal
        show={showInitTemplateModal}
        onHide={() => !sendingInitTemplate && setShowInitTemplateModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton style={{ backgroundColor: '#0f172a', borderColor: '#1e293b', color: '#ffffff' }}>
          <Modal.Title className="fs-6 fw-bold text-white d-flex align-items-center gap-2">
            <FileText size={18} style={{ color: '#a855f7' }} />
            <span>
              {initModalMode === 'chat'
                ? 'Enviar Plantilla de Inicio de Conversación'
                : 'Nueva Conversación WhatsApp (Buscar Pasajero o Número)'}
            </span>
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSendInitTemplate}>
          <Modal.Body className="p-3" style={{ backgroundColor: '#0b1120', color: '#e2e8f0' }}>
            {/* Selector de modo: Chat Actual vs Buscar otro pasajero / número */}
            <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom" style={{ borderColor: '#1e293b' }}>
              <div className="small text-secondary">
                Plantilla oficial Meta: <code className="text-info">inicio_conversacion_emilia</code> (2 variables editables)
              </div>
              {initModalMode === 'chat' ? (
                <Button
                  size="sm"
                  variant="outline-info"
                  style={{ fontSize: '0.72rem' }}
                  onClick={() => {
                    setInitModalMode('search');
                    setContactSearchQuery('');
                    setContactSearchResults([]);
                  }}
                >
                  🔍 Buscar otro pasajero / número
                </Button>
              ) : selectedChat ? (
                <Button
                  size="sm"
                  variant="outline-secondary"
                  style={{ fontSize: '0.72rem', color: '#cbd5e1', borderColor: '#475569' }}
                  onClick={() => handleOpenChatTemplateModal(selectedChat)}
                >
                  ↩ Usar contacto del chat abierto ({selectedChat.contact_name || selectedChat.phone_number})
                </Button>
              ) : null}
            </div>

            {/* Buscador de pasajero histórico o ingreso manual de teléfono */}
            {initModalMode === 'search' && (
              <div className="mb-3 p-3 rounded-3" style={{ backgroundColor: '#0f172a', border: '1px solid #1e293b' }}>
                <Form.Label className="small fw-bold text-white mb-1">
                  1. Buscar pasajero histórico (chats o viajes anteriores) o escribir teléfono
                </Form.Label>
                <InputGroup size="sm">
                  <InputGroup.Text style={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#94a3b8' }}>
                    <Search size={14} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Escribe el nombre de un pasajero o un número (+569...)"
                    style={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#ffffff' }}
                    value={contactSearchQuery}
                    onChange={e => {
                      const val = e.target.value;
                      setContactSearchQuery(val);
                      if (/^\+?[\d\s()-]{6,}$/.test(val.trim())) {
                        setTemplatePhone(val.trim());
                      }
                    }}
                    autoFocus
                  />
                  {searchingContacts && (
                    <InputGroup.Text style={{ backgroundColor: '#1e293b', borderColor: '#334155' }}>
                      <Spinner size="sm" animation="border" style={{ color: '#a855f7' }} />
                    </InputGroup.Text>
                  )}
                </InputGroup>

                {/* Resultados de búsqueda histórica */}
                {contactSearchResults.length > 0 && (
                  <div
                    className="mt-2 rounded-2 overflow-auto"
                    style={{ maxHeight: '190px', backgroundColor: '#1e293b', border: '1px solid #334155' }}
                  >
                    {contactSearchResults.map((item, idx) => (
                      <div
                        key={`${item.phone_number}-${idx}`}
                        className="p-2 d-flex justify-content-between align-items-center border-bottom gap-2"
                        style={{ borderColor: '#334155', cursor: 'pointer' }}
                        onClick={() => handleSelectContactFromSearch(item)}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#2e1065')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                      >
                        <div>
                          <div className="fw-bold text-white small">{item.contact_name || 'Sin nombre'}</div>
                          <div className="text-info" style={{ fontSize: '0.75rem' }}>+{item.phone_number?.replace(/^\+/, '')}</div>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <Badge bg={item.source === 'chat' ? 'primary' : 'secondary'} style={{ fontSize: '0.65rem' }}>
                            {item.source === 'chat' ? '💬 Chat previo' : '🚗 Historial Viajes'}
                          </Badge>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline-info"
                            className="py-0 px-2 fw-bold"
                            style={{ fontSize: '0.68rem' }}
                            title="Abrir conversación solo para leer mensajes anteriores sin enviar plantilla"
                            onClick={e => {
                              e.stopPropagation();
                              handleOpenReadOnlyChat(item);
                            }}
                          >
                            👁️ Solo leer
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Teléfono Destinatario */}
            <div className="row g-3 mb-3">
              <div className="col-md-5">
                <Form.Label className="small fw-bold text-white mb-1">
                  📱 Número de WhatsApp Destino
                </Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder="Ej: +56912345678"
                  style={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#ffffff' }}
                  value={templatePhone}
                  onChange={e => setTemplatePhone(e.target.value)}
                  required
                />
                <Form.Text style={{ color: '#94a3b8', fontSize: '0.7rem' }}>
                  Incluye código de país (ej. 569... o 549...).
                </Form.Text>
              </div>

              {/* Variable {{1}}: Nombre del pasajero */}
              <div className="col-md-7">
                <Form.Label className="small fw-bold text-white mb-1">
                  ✏️ Variable {'{{1}}'} — Nombre del Pasajero / Contacto
                </Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder="Ej: Juan Pérez (o Estimado pasajero)"
                  style={{ backgroundColor: '#1e293b', borderColor: '#7c3aed', color: '#ffffff' }}
                  value={templateVar1Name}
                  onChange={e => setTemplateVar1Name(e.target.value)}
                  required
                />
                <Form.Text style={{ color: '#94a3b8', fontSize: '0.7rem' }}>
                  Aparecerá después de &quot;Hola {'{{1}}'}, somos de Transportes Emilia...&quot;
                </Form.Text>
              </div>
            </div>

            {/* Variable {{2}}: Mensaje / Tema a tratar */}
            <div className="mb-3">
              <Form.Label className="small fw-bold text-white mb-1">
                ✏️ Variable {'{{2}}'} — Mensaje o Tema a informar (Editable por Locutora)
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Ej: Necesitamos confirmar la dirección exacta de recogida para su traslado programado hoy a las 18:30 hrs."
                style={{ backgroundColor: '#1e293b', borderColor: '#7c3aed', color: '#ffffff', fontSize: '0.88rem' }}
                value={templateVar2Message}
                onChange={e => setTemplateVar2Message(e.target.value)}
                required
              />
            </div>

            {/* Vista Previa en Vivo de la Plantilla */}
            <div className="p-3 rounded-3" style={{ backgroundColor: '#1e293b', border: '1px solid #475569' }}>
              <div className="small fw-bold mb-1" style={{ color: '#c084fc', fontSize: '0.74rem' }}>
                👁️ VISTA PREVIA DEL MENSAJE QUE RECIBIRÁ EL PASAJERO:
              </div>
              <div className="p-2.5 rounded-2 text-white" style={{ backgroundColor: '#3b0764', border: '1px solid #6b21a8', whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>
                {`Hola ${templateVar1Name.trim() || '{{1}}'}, somos de *Transportes Emilia*. Nos comunicamos con usted respecto a su servicio de traslado para informarle lo siguiente:\n\n${templateVar2Message.trim() || '{{2}}'}\n\nQuedamos atentos a su confirmación por este medio.\n*Transportes Emilia*`}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer style={{ backgroundColor: '#0f172a', borderColor: '#1e293b' }}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowInitTemplateModal(false)}
              disabled={sendingInitTemplate}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="submit"
              className="fw-bold d-flex align-items-center gap-2 px-3"
              style={{ backgroundColor: '#7c3aed', borderColor: '#6d28d9' }}
              disabled={sendingInitTemplate || !templateVar2Message.trim() || !(templatePhone || contactSearchQuery).trim()}
            >
              {sendingInitTemplate ? (
                <>
                  <Spinner size="sm" animation="border" />
                  <span>Enviando...</span>
                </>
              ) : (
                <>
                  <Send size={15} />
                  <span>🚀 Enviar Plantilla</span>
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal para ver imagen recibida en tamaño completo */}
      <Modal
        show={!!previewImageUrl}
        onHide={() => setPreviewImageUrl(null)}
        centered
        size="lg"
      >
        <Modal.Header closeButton style={{ backgroundColor: '#0f172a', borderColor: '#1e293b', color: '#ffffff' }}>
          <Modal.Title className="fs-6 fw-bold text-white">📷 Imagen enviada por WhatsApp</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-3" style={{ backgroundColor: '#090d16' }}>
          {previewImageUrl && (
            <img
              src={previewImageUrl}
              alt="Vista completa"
              style={{ maxWidth: '100%', maxHeight: '78vh', objectFit: 'contain', borderRadius: '8px' }}
            />
          )}
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#0f172a', borderColor: '#1e293b' }}>
          {previewImageUrl && (
            <Button
              variant="outline-info"
              size="sm"
              as="a"
              href={previewImageUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Abrir original en nueva pestaña
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={() => setPreviewImageUrl(null)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default WhatsAppChatCenter;
